/* ==========================================================================
   GrindGuard Setup Wizard
   --------------------------------------------------------------------------
   PHASE 2 — the real engine.

   Flow / State / Router / Renderer, reading assets/wizard-flow.json. Step
   types covered: content, choice, toggles, checkpoint. Deep linking, resume,
   and conditional routing are all real and exercised by the PREP flow.

   NOT yet here (later phases): Timer, Chain (auto-advance), Forms
   (escalation/replacement), triage branches, MOLD/CHECK/CARE content.

   Plain ES2019. No dependencies, no build step. See ENGINE_SPEC.md for the
   full specification this implements a subset of.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_ID = 'gg-wizard';
  var STATE_KEY = 'gg_wizard_state';
  var PREFS_KEY = 'gg_wizard_prefs';
  var STATE_VERSION = 1;
  var STATE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days, per ENGINE_SPEC §4

  // ------------------------------------------------------------------
  // Config
  // ------------------------------------------------------------------

  function readConfig() {
    var el = document.getElementById('gg-wz-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[wizard] config JSON is malformed', err);
      return {};
    }
  }

  var config = readConfig();

  // ------------------------------------------------------------------
  // Storage — must never throw. Safari private mode and a full quota both
  // reject writes, and the wizard has to keep working without persistence.
  // ------------------------------------------------------------------

  var store = {
    get: function (key, fallback) {
      try {
        var raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (err) {
        return fallback;
      }
    },
    set: function (key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (err) {
        return false;
      }
    },
    clear: function (key) {
      try {
        window.localStorage.removeItem(key);
      } catch (err) {
        /* nothing useful to do */
      }
    }
  };

  function genSessionId() {
    return 'wz_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  // ------------------------------------------------------------------
  // State
  //
  // Two independent stores, per ENGINE_SPEC §4: `prefs` (sound/haptic
  // opt-in — asked once, applies forever) and the wizard's own progress
  // state (versioned, TTL'd, safe to discard).
  // ------------------------------------------------------------------

  // Sound/haptic start OFF for a genuinely fresh session — nothing has audio
  // "by default" the way some apps do, which people instinctively reach to
  // mute. The FIRST time a prefs-store toggles step (prep.experience) is
  // shown, buildToggles() seeds sound/haptic to ON from that step's own
  // schema defaults and flips `asked` true — so the customer sees them
  // already on, as a suggestion, and can turn either off before continuing.
  // `asked` guards that seed from re-firing on a later revisit (e.g. via
  // Back), where it should show whatever they actually chose instead.
  var prefs = store.get(PREFS_KEY, { sound: false, haptic: false, asked: false });
  function savePrefs() { store.set(PREFS_KEY, prefs); }

  var State = {
    data: null,

    /**
     * Loads saved state if it's usable: right schema version, right flow
     * version, and not expired. Any mismatch is treated as "start fresh"
     * rather than attempted migration — the session is short enough that a
     * half-migrated flow costs more than it saves. See ENGINE_SPEC §4.
     */
    load: function () {
      var saved = store.get(STATE_KEY, null);
      if (!saved) return null;
      if (saved.v !== STATE_VERSION) return null;
      if (saved.flowVersion !== flow.flowVersion) return null;
      if (Date.now() - (saved.updatedAt || 0) > STATE_TTL_MS) return null;
      return saved;
    },

    init: function (resumed) {
      this.data = resumed || {
        v: STATE_VERSION,
        flowVersion: flow.flowVersion,
        sessionId: genSessionId(),
        startedAt: Date.now(),
        updatedAt: Date.now(),
        currentStep: flow.settings.startStep,
        history: [],
        data: JSON.parse(JSON.stringify(flow.initialState || {}))
      };
      this.save();
    },

    save: function () {
      this.data.updatedAt = Date.now();
      store.set(STATE_KEY, this.data);
    },

    reset: function () {
      store.clear(STATE_KEY);
      this.init(null);
    },

    /** Dotted-path get, e.g. "guards.large" or "safetyFlags.length". */
    get: function (path, fallback) {
      var parts = String(path).split('.');
      var val = this.data.data;
      for (var i = 0; i < parts.length; i++) {
        if (val == null) return fallback;
        if (parts[i] === 'length' && Array.isArray(val)) return val.length;
        val = val[parts[i]];
      }
      return val === undefined ? fallback : val;
    },

    /** Shallow-merges a flat { key: value } object into state.data. */
    set: function (patch) {
      if (!patch) return;
      for (var k in patch) {
        if (Object.prototype.hasOwnProperty.call(patch, k)) this.data.data[k] = patch[k];
      }
      this.save();
    },

    /**
     * Dotted-path increment/decrement — e.g. `increment("guards.large", -1)`.
     * Needed alongside `set` (a static overwrite) for anything that
     * accumulates: attempt counts, guard inventory, the heat-time bias a
     * triage remedy nudges. Unlike `set`, this walks into (and creates, if
     * missing) intermediate objects rather than clobbering siblings — a
     * shallow `set({guards:{large:1}})` would silently wipe `guards.small`.
     */
    increment: function (path, delta) {
      var parts = String(path).split('.');
      var obj = this.data.data;
      for (var i = 0; i < parts.length - 1; i++) {
        if (typeof obj[parts[i]] !== 'object' || obj[parts[i]] === null) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      var last = parts[parts.length - 1];
      obj[last] = (Number(obj[last]) || 0) + delta;
      this.save();
    },

    pushHistory: function (stepId) {
      this.data.history.push(stepId);
      if (this.data.history.length > 50) this.data.history.shift();
    },

    goto: function (stepId) {
      this.data.currentStep = stepId;
      this.save();
    }
  };

  // ------------------------------------------------------------------
  // Condition evaluator — deliberately small. See ENGINE_SPEC §3 "Routing".
  // Supports eq/neq/gt/gte/lt/lte/in/and/or/not over dotted state paths,
  // with a virtual `.length` accessor for arrays (used by the safety-flag
  // branch below).
  // ------------------------------------------------------------------

  /**
   * Established defaults for the personalization fields that materially
   * change downstream copy/branching if left genuinely unset — 'small' and
   * 'upper' match prep.size/prep.arch's own "Most common" badges (real
   * purchase-data conventions, not guesses); 'kettle' matches
   * prep.heatmethod's own "Most common" badge. This is the SINGLE source of
   * truth for all three — see resolveTimerSeconds and the replacement-form
   * size pre-fill in buildActionButton/wireEscalationOverlay, which used to
   * each hardcode their own literal 'large' independently before 20 Aug;
   * all now read this constant instead, so there's one place to change the
   * assumption, not five. Consulted only when the real state value is
   * absent — never overwrites an actual customer choice, however old, since
   * these are read-time fallbacks, not writes to State.data. Exists for one
   * concrete reason: a customer can reach deep CHECK-phase triage screens
   * (an expired/cleared session on the normal path, or — before 20 Aug's
   * support.triage_size/_arch steps existed — the support-team hidden entry
   * point) having never answered prep.size/prep.arch at all. Without this,
   * copy tokens render blank ("Place it over your  teeth") and one triage
   * branch (triage.gag_switch_arch) has no visible primary action at all —
   * both silently broken, not just cosmetically off. `heatMethod` doesn't
   * strictly need an entry here — every existing condition checks
   * `neq: ["heatMethod", "stovetop"]` rather than `eq: [..., "kettle"]`, so
   * unset already falls through to kettle copy by construction — but it's
   * included anyway so that stays true even if a future step is authored
   * the other way round.
   */
  var STATE_UNSET_DEFAULTS = { size: 'small', arch: 'upper', heatMethod: 'kettle' };

  /**
   * Resolves a `{token}` inside a dotted state path to another state value —
   * e.g. `"guards.{size}"` → `"guards.large"` when `state.size === "large"`.
   * Needed because a triage remedy's "use your spare" only knows it wants
   * "the spare count for whichever size this customer is on," not a literal
   * branch per size. Same `{token}` syntax as copy-text `interpolate()`, just
   * applied to a path instead of a rendered string — kept separate since the
   * two run at very different points (path resolution happens on every
   * condition/increment call, not just at render time).
   */
  function resolvePathTokens(path) {
    return String(path).replace(/\{(\w+)\}/g, function (m, key) {
      var v = State.get(key);
      if (v == null && STATE_UNSET_DEFAULTS[key] !== undefined) v = STATE_UNSET_DEFAULTS[key];
      return v != null ? v : m;
    });
  }

  function resolveOperand(operand) {
    if (typeof operand === 'string') {
      if (operand.indexOf('{') !== -1) operand = resolvePathTokens(operand);
      if (/^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)*$/.test(operand)) {
        // Looks like a state path. Try it; if genuinely undefined AND not a
        // recognisable literal, callers get `undefined`, which is fine —
        // every operator below treats undefined as "doesn't match". A bare
        // top-level `size`/`arch` operand gets STATE_UNSET_DEFAULTS applied
        // first, same reasoning as resolvePathTokens above.
        var val = State.get(operand, undefined);
        if (val === undefined && STATE_UNSET_DEFAULTS[operand] !== undefined) val = STATE_UNSET_DEFAULTS[operand];
        if (val !== undefined) return val;
      }
    }
    return operand;
  }

  function evalCondition(cond) {
    if (!cond || typeof cond !== 'object') return false;

    if (cond.and) return cond.and.every(evalCondition);
    if (cond.or) return cond.or.some(evalCondition);
    if (cond.not) return !evalCondition(cond.not);

    var op = Object.keys(cond)[0];
    var args = cond[op];
    if (!Array.isArray(args) || args.length < 2) return false;

    var a = resolveOperand(args[0]);
    var b = resolveOperand(args[1]);

    switch (op) {
      case 'eq': return a === b;
      case 'neq': return a !== b;
      case 'gt': return Number(a) > Number(b);
      case 'gte': return Number(a) >= Number(b);
      case 'lt': return Number(a) < Number(b);
      case 'lte': return Number(a) <= Number(b);
      case 'in': return Array.isArray(b) ? b.indexOf(a) !== -1 : false;
      default:
        console.warn('[wizard] unknown condition operator:', op);
        return false;
    }
  }

  // ------------------------------------------------------------------
  // Router
  // ------------------------------------------------------------------

  // Captured at boot when a resumable session exists, BEFORE arrive.resume is
  // rendered. It has to live outside State, because rendering arrive.resume
  // itself calls State.goto('arrive.resume'), which overwrites
  // state.data.currentStep — so by the time the customer taps "Continue",
  // the very value we need has already been clobbered by navigating to the
  // prompt that asks the question. Caught by walking the flow end-to-end
  // rather than assuming it worked.
  var resumeTarget = null;

  var Router = {
    /**
     * `next` is either a plain step id, or an array of
     * { if: <condition>, then: <stepId> } rules evaluated in order, with a
     * final unconditional `{ then }` as the default. Two sentinel values are
     * handled specially: __resume__ (continue into the saved step) and
     * __restart__ (wipe state, go to startStep) — see arrive.resume.
     */
    resolve: function (next) {
      var target;

      if (typeof next === 'string') {
        target = next;
      } else if (Array.isArray(next)) {
        target = null;
        for (var i = 0; i < next.length; i++) {
          var rule = next[i];
          if (!rule.if || evalCondition(rule.if)) { target = rule.then; break; }
        }
        if (!target) {
          console.warn('[wizard] no matching route and no default; staying put');
          return null;
        }
      } else {
        return null;
      }

      if (target === '__resume__') return resumeTarget || flow.settings.startStep;
      if (target === '__restart__') {
        resumeTarget = null;
        State.reset();
        return flow.settings.startStep;
      }
      return target;
    }
  };

  // ------------------------------------------------------------------
  // Cues — audio, haptics. Nothing fires until prep.experience (or the
  // header toggle) sets prefs.asked = true. See ENGINE_SPEC §6.2.
  // ------------------------------------------------------------------

  var cues = {
    ctx: null,

    unlock: function () {
      if (this.ctx) return;
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      try {
        this.ctx = new Ctx();
      } catch (err) {
        this.ctx = null;
      }
    },

    /**
     * beep() re-attempts resume() on every call, rather than trusting the
     * one-off resume() from unlock(). Root-caused a "sound stopped working
     * entirely" report on real iOS: a single early resume() call — fired
     * from a generic root-level pointerdown, not tied to the specific
     * gesture that plays the tone — can silently fail to actually start the
     * context, and it then stays 'suspended' forever with no further retry.
     * Every beep() runs inside a real click handler, which is a legitimate
     * user gesture Safari will always honour a resume() call within — so
     * retrying here is strictly more reliable than relying on the first one.
     *
     * NOTE: this fixes the *software* failure mode. It does not and cannot
     * override the iPhone's hardware silent switch — Web Audio tones use the
     * 'ambient' audio session category by default, which respects it. If
     * sound still doesn't play with the switch off, that's the switch, not
     * the code.
     */
    beep: function (freq, ms) {
      if (!prefs.sound) return;
      if (!this.ctx) this.unlock();
      if (!this.ctx) return;
      var ctx = this.ctx;
      var play = function () {
        try {
          var osc = ctx.createOscillator();
          var gain = ctx.createGain();
          osc.frequency.value = freq || 880;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.0001, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (ms || 150) / 1000);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + (ms || 150) / 1000 + 0.02);
        } catch (err) { /* audio is an enhancement — never let it break a step */ }
      };
      if (ctx.state === 'suspended') {
        ctx.resume().then(play).catch(function () { /* still nothing we can do */ });
      } else {
        play();
      }
    },

    buzz: function (pattern) {
      // iOS Safari has no Vibration API at all — confirmed against an
      // independent third-party test page during Phase 1 device testing, so
      // this is a platform gap, not a bug. Audio carries the load there.
      if (!prefs.haptic || !navigator.vibrate) return;
      try { navigator.vibrate(pattern || 40); } catch (err) { /* ignore */ }
    },

    /** A short two-note chime, distinct from the single-tone beep() used for
     * toggle previews — this is what a timer's start/end actually plays. */
    chime: function (ascending) {
      this.beep(ascending ? 660 : 880, 110);
      var self = this;
      setTimeout(function () { self.beep(ascending ? 880 : 660, 180); }, 130);
    },

    /** The 3-2-1 lead-in into an automatic step change — a short, high,
     * percussive blip, deliberately unlike the timer chime so the two read
     * as different events. Pitch rises slightly on the final tick (n===1)
     * so "about to move" is audible, not just visible. */
    tick: function (n) {
      this.beep(n === 1 ? 1320 : 990, 70);
    }
  };

  // ------------------------------------------------------------------
  // Wake Lock — keeps the screen on for the whole touchless MOLD sequence.
  // PHASE 3, hardened after a real-device report: after looping back through
  // a second heat cycle, the screen slept mid-flow and auto-advance stalled
  // with it — a wake-lock re-acquisition failure, not a JS logic bug (the
  // underlying setInterval/endsAt math is sound regardless).
  //
  // Rebuilt around a `wantHeld` flag as the single source of truth for
  // "should a lock be held right now", rather than a one-shot "please
  // re-request" flag. Every resume signal — visibilitychange, pageshow,
  // window focus — just asks "do we still want it?" and retries if so.
  // Genuinely re-entrant: any number of grant/lose/regrant cycles converge
  // on the same answer, which a one-shot flag can't guarantee. Also retries
  // once on an outright request() rejection — iOS Safari's implementation is
  // known to be flaky on rapid back-to-back (re-)acquisition.
  // ------------------------------------------------------------------

  var wakeLockCtl = (function () {
    var sentinel = null;
    var supported = 'wakeLock' in navigator;
    var wantHeld = false;
    var retried = false;

    function attempt() {
      if (!wantHeld || !supported || !config.features || !config.features.wakeLock) return;
      if (document.visibilityState !== 'visible') return; // will retry on the next resume signal
      if (sentinel) return; // already holding one
      try {
        navigator.wakeLock.request('screen').then(function (s) {
          sentinel = s;
          retried = false;
          s.addEventListener('release', function () {
            sentinel = null;
            // Only the browser can release a lock out from under us (screen
            // lock, OS policy) — if we still want it, try once to get it
            // straight back, per the spec's own "degrade, don't loop
            // forever" guidance.
            if (wantHeld && !retried) { retried = true; setTimeout(attempt, 250); }
          });
        }).catch(function () {
          if (!retried) { retried = true; setTimeout(attempt, 400); }
        });
      } catch (err) { /* nothing more we can do */ }
    }

    function request() { wantHeld = true; retried = false; attempt(); }

    function release() {
      wantHeld = false;
      if (sentinel) { try { sentinel.release(); } catch (err) { /* ignore */ } sentinel = null; }
    }

    function onResume() { if (document.visibilityState === 'visible') attempt(); }
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('focus', onResume);

    return { request: request, release: release, supported: supported };
  })();

  // ------------------------------------------------------------------
  // Timer — the core of Phase 3. Timestamp-based, not tick-based: stores
  // `endsAt` (+ the original duration, for coaching maths) and computes
  // remaining from Date.now() on every animation frame, so it can't drift
  // and survives the tab backgrounding that setInterval-based timers don't.
  // See ENGINE_SPEC.md §6.1.
  //
  // Persists its own state into State.data.data._timer so a page reload
  // mid-countdown resumes correctly rather than silently restarting or
  // showing nothing — this matters more here than almost anywhere else in
  // the wizard, since these are the steps a mistake is expensive on.
  // ------------------------------------------------------------------

  var Timer = (function () {
    // Driven by setInterval, not requestAnimationFrame, despite ENGINE_SPEC
    // §6.1 originally calling for rAF. Corrected during Phase 3 testing:
    // rAF is throttled to a full stop in backgrounded tabs by design — real
    // risk here, since a customer switching apps mid-timer to check a text
    // is entirely normal, not an edge case. rAF's usual advantage (smoother
    // animation) doesn't apply to a whole-second countdown display, and its
    // usual justification (avoiding drift) was already handled independently
    // — `remaining` is always computed fresh from `endsAt - Date.now()`,
    // never by counting fixed ticks, so neither scheduling mechanism can
    // drift. setInterval gets throttled to ~1/sec in the background rather
    // than fully pausing, which is what "survives backgrounding" actually
    // requires. A visibilitychange listener below forces one immediate tick
    // on return, so there's no residual lag either.
    var TICK_MS = 200;
    var intervalId = null;
    var endsAt = 0;
    var totalMs = 0;
    var coaching = [];
    var lastCoachIndex = -1;
    var onTick = null, onComplete = null, onCoach = null;
    var completed = false;
    var activeStepId = null;

    function persist() {
      State.data.data._timer = { step: activeStepId, endsAt: endsAt, totalMs: totalMs };
      State.save();
    }
    function clearPersisted() {
      if (State.data && State.data.data) delete State.data.data._timer;
      State.save();
    }

    function fireCoachingUpTo(elapsedSec, announce) {
      for (var i = 0; i < coaching.length; i++) {
        if (coaching[i].at <= elapsedSec && i > lastCoachIndex) lastCoachIndex = i;
      }
      if (announce && lastCoachIndex >= 0 && onCoach) onCoach(coaching[lastCoachIndex], lastCoachIndex);
    }

    function tick() {
      var remaining = endsAt - Date.now();
      if (remaining <= 0) { finish(); return; }

      if (onTick) onTick(remaining, totalMs);

      var elapsedSec = (totalMs - remaining) / 1000;
      var before = lastCoachIndex;
      fireCoachingUpTo(elapsedSec, false);
      if (lastCoachIndex !== before && onCoach) onCoach(coaching[lastCoachIndex], lastCoachIndex);
    }

    // Collapses whatever throttled gap just happened into one immediate,
    // correct tick — the customer shouldn't see a stale number for even a
    // moment after switching back. Listens on all three resume signals, not
    // just visibilitychange — iOS Safari doesn't fire it with perfect
    // reliability coming out of a deep suspend (screen-locked, not just
    // backgrounded), where pageshow/focus are the more dependable signal.
    function catchUp() { if (document.visibilityState === 'visible' && intervalId !== null) tick(); }
    document.addEventListener('visibilitychange', catchUp);
    window.addEventListener('pageshow', catchUp);
    window.addEventListener('focus', catchUp);

    function finish() {
      if (completed) return;
      completed = true;
      if (intervalId !== null) { clearInterval(intervalId); intervalId = null; }
      // No wakeLockCtl call here — the lock now spans the whole touchless
      // sequence (see step.holdWake in Wizard.render), not each individual
      // timer. Releasing and re-requesting between every step was extra
      // acquisition churn for no benefit, and iOS's wake lock is flaky
      // enough on rapid re-acquisition that it's worth avoiding entirely.
      clearPersisted();
      cues.chime(false); // end — descending, more assertive than the start chime
      cues.buzz([70, 50, 70]);
      if (onTick) onTick(0, totalMs);
      if (onComplete) onComplete();
    }

    return {
      start: function (stepId, seconds, opts) {
        opts = opts || {};
        activeStepId = stepId;
        coaching = (opts.coaching || []).slice().sort(function (a, b) { return a.at - b.at; });
        lastCoachIndex = -1;
        completed = false;
        totalMs = Math.max(1, seconds) * 1000;
        endsAt = Date.now() + totalMs;
        onTick = opts.onTick || null;
        onComplete = opts.onComplete || null;
        onCoach = opts.onCoach || null;
        persist();
        cues.chime(true); // start — ascending, softer
        if (intervalId !== null) clearInterval(intervalId);
        tick();
        intervalId = setInterval(tick, TICK_MS);
      },

      /** Returns false if there's nothing valid to resume (different step,
       * or the persisted window already elapsed) — caller falls back to
       * start(). */
      resume: function (stepId, opts) {
        var saved = State.data.data._timer;
        if (!saved || saved.step !== stepId) return false;
        var remaining = saved.endsAt - Date.now();
        if (remaining <= 0) { clearPersisted(); return false; }

        opts = opts || {};
        activeStepId = stepId;
        coaching = (opts.coaching || []).slice().sort(function (a, b) { return a.at - b.at; });
        completed = false;
        endsAt = saved.endsAt;
        totalMs = saved.totalMs;
        onTick = opts.onTick || null;
        onComplete = opts.onComplete || null;
        onCoach = opts.onCoach || null;

        // Fast-forward coaching to whatever stage we should already be in —
        // without this, resuming mid-`mold.shape` would show the "press"
        // instruction from second 0 even if we're actually at second 25.
        lastCoachIndex = -1;
        fireCoachingUpTo((totalMs - remaining) / 1000, true);

        if (intervalId !== null) clearInterval(intervalId);
        tick();
        intervalId = setInterval(tick, TICK_MS);
        return true;
      },

      stop: function () {
        if (intervalId !== null) { clearInterval(intervalId); intervalId = null; }
        activeStepId = null;
        clearPersisted();
      },

      restart: function (stepId, seconds, opts) {
        this.stop();
        this.start(stepId, seconds, opts);
      },

      /**
       * A buffer nudge, not a do-over — pushes the deadline out without
       * touching progress or coaching state, replacing the old Restart
       * control (which threw away everything and started the whole stage
       * over, a genuinely destructive option to leave sitting next to a
       * countdown someone's hands are full during).
       */
      extend: function (seconds) {
        if (intervalId === null) return;
        var deltaMs = Math.max(0, seconds) * 1000;
        endsAt += deltaMs;
        totalMs += deltaMs;
        persist();
        tick();
      },

      isRunning: function () { return intervalId !== null; },
      activeStep: function () { return activeStepId; }
    };
  })();

  // ?fast=1 (only honoured alongside ?preview=1) divides every timer duration
  // down for testing — sitting through a real 2-minute cool-down timer a
  // dozen times while verifying this phase would not be a good use of
  // anyone's afternoon. Never active outside the debug context.
  var DEBUG_FAST = /[?&]preview=1/.test(location.search) && /[?&]fast=1/.test(location.search);

  /**
   * Resolves a timer step's duration in seconds from config (section
   * settings), applying the size-dependent lookup for `durationKey: "heat"`
   * and any state-driven bias (triage sets `heatBias`; ±2s on loose/tight,
   * −3..4s on a recoverable over-soft remold — see CANONICAL_FACTS.md).
   */
  function resolveTimerSeconds(spec) {
    var base;
    if (spec.durationKey === 'heat') {
      var size = State.get('size', STATE_UNSET_DEFAULTS.size);
      base = Number(config.timers && config.timers[size === 'small' ? 'heatSmall' : 'heatLarge']);
    } else if (spec.durationKey) {
      // waterCool used to branch by heatMethod (microwave got its own
      // shorter duration) — removed 19 Aug along with the microwave method
      // itself. Stovetop shares the kettle's exact cooldown, so this is
      // just a plain lookup now, same as every other durationKey.
      base = Number(config.timers && config.timers[spec.durationKey]);
    } else {
      base = Number(spec.duration);
    }
    if (!isFinite(base) || base <= 0) base = 30;

    var bias = spec.biasFrom ? (Number(State.get(spec.biasFrom, 0)) || 0) : 0;
    var seconds = Math.max(3, Math.round(base + bias));

    if (DEBUG_FAST) seconds = Math.max(2, Math.round(seconds / 8));
    return seconds;
  }

  // ------------------------------------------------------------------
  // Analytics
  // ------------------------------------------------------------------

  function track(event, payload) {
    window.dataLayer = window.dataLayer || [];
    var data = { event: event };
    for (var k in payload) if (Object.prototype.hasOwnProperty.call(payload, k)) data[k] = payload[k];
    window.dataLayer.push(data);
    if (debug.on) debug.log(event + ' ' + JSON.stringify(payload || {}));
  }

  // ------------------------------------------------------------------
  // Copy tokens — {size}, {sizeLabel}, {archLabel} etc. Deliberately tiny:
  // just enough to make cp.ready_to_mold's recap and prep.testfit's "your
  // {size} guard" line work. Anything fancier belongs in a real template
  // engine, which this isn't trying to be.
  // ------------------------------------------------------------------

  function labelize(value, map) {
    return (map && map[value]) || value || '';
  }

  /**
   * Countdown display — plain seconds ("22") or m:ss ("1:20"), decided by
   * the caller once per stage (see wireTimer's `useClockFormat`) rather
   * than re-evaluated against the current remaining value every tick,
   * which would flip formats mid-countdown the moment a long stage
   * crossed under a minute remaining.
   */
  function formatCountdown(sec, asClock) {
    sec = Math.max(0, Math.ceil(sec));
    if (!asClock) return String(sec);
    var m = Math.floor(sec / 60), s = sec % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  /**
   * Regional-vocabulary tokens — `{{mold}}` etc., double-braced to keep them
   * visually and mechanically distinct from the single-brace `{token}` state
   * tokens below (`{sizeLabel}` etc.): one substitutes a piece of the
   * customer's own state, the other picks the right word for their market.
   * Resolved from `config.strings` against `config.market` (Shopify's
   * `localization.country.iso_code`, an uppercase ISO code like "GB"/"AU").
   * `default` is the US-English baseline per QUESTIONS.md H4 ("finalise
   * copy from an American standpoint, then build the regional list") — any
   * market without its own entry falls back to it. An unrecognised key is
   * left untouched rather than silently dropped, so a typo shows up as
   * literal `{{text}}` in testing instead of vanishing. See
   * WIZARD_FLOW_SCHEMA.md and ENGINE_SPEC.md §9.6.
   */
  function resolveStrings(str) {
    var market = (config.market || '').toUpperCase();
    return str.replace(/\{\{(\w+)\}\}/g, function (m, key) {
      var entry = config.strings && config.strings[key];
      if (!entry) return m;
      return entry[market] || entry.default || m;
    });
  }

  function interpolate(str) {
    if (typeof str !== 'string') return str;
    str = resolveStrings(str);
    var size = State.get('size', STATE_UNSET_DEFAULTS.size);
    var arch = State.get('arch', STATE_UNSET_DEFAULTS.arch);
    var tokens = {
      size: size,
      arch: arch,
      sizeLabel: labelize(size, { small: 'Small', large: 'Large' }),
      archLabel: labelize(arch, { upper: 'upper', lower: 'lower' })
    };
    return str.replace(/\{(\w+)\}/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : m;
    });
  }

  // ------------------------------------------------------------------
  // Flow — loaded once at boot from wizard-flow.json.
  // ------------------------------------------------------------------

  var flow = null;

  function loadFlow() {
    var url = config.flowUrl;
    if (!url) return Promise.reject(new Error('no flowUrl in config'));
    return fetch(url, { credentials: 'omit' }).then(function (res) {
      if (!res.ok) throw new Error('flow fetch failed: ' + res.status);
      return res.json();
    });
  }

  // ------------------------------------------------------------------
  // Renderer — step types: content, choice, toggles, checkpoint.
  // Timer/chain/form types land in later phases.
  // ------------------------------------------------------------------

  function el(tag, className, html) {
    var e = document.createElement(tag);
    if (className) e.className = className;
    if (html != null) e.innerHTML = html;
    return e;
  }

  /**
   * Shared markup for a media placeholder. Phase 9 progressively replaces
   * these with delivered assets, but keeping a real fallback is important:
   * a later flow edit must never turn a missing asset into a broken image.
   */
  function mediaPlaceholderHtml(media) {
    return '<div class="gg-wz-media gg-wz-media--placeholder">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="m9 10 5 2-5 2z"/></svg>' +
      '<span>' + ((media && media.label) || 'Animation') + '</span>' +
      '</div>';
  }

  /**
   * Asset rendering is deliberately centralised rather than spread through
   * the step renderers. `wizard-flow.json` only names a stable media key;
   * Liquid resolves that key to Shopify's fingerprinted asset URL in the
   * section config. That keeps flow editing declarative and lets Shopify
   * change CDN URLs without touching the flow file.
   *
   * Lottie is loaded only when an animated asset is actually rendered. The
   * SVG poster remains visible underneath it, so reduced-motion users and any
   * player/network failure get a useful, deliberate still rather than a blank
   * box. The player is our own Shopify asset — never a third-party CDN.
   */
  var Media = (function () {
    var playerPromise = null;

    function assetUrl(key) {
      return key && config.media && config.media.assets && config.media.assets[key];
    }

    function placeholder(media) {
      var wrap = document.createElement('div');
      wrap.innerHTML = mediaPlaceholderHtml(media);
      return wrap.firstElementChild;
    }

    function image(src, alt, className) {
      var img = document.createElement('img');
      img.className = className || 'gg-wz-media-image';
      img.src = src;
      img.alt = alt || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      return img;
    }

    function setAspect(el, media, fallback) {
      el.style.aspectRatio = (media && media.aspect) || fallback || '1 / 1';
    }

    function loadPlayer() {
      if (window.lottie && typeof window.lottie.loadAnimation === 'function') {
        return Promise.resolve(window.lottie);
      }
      if (playerPromise) return playerPromise;

      playerPromise = new Promise(function (resolve, reject) {
        var src = config.media && config.media.lottiePlayer;
        if (!src) { reject(new Error('no Lottie player configured')); return; }
        var script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = function () {
          var player = window.lottie || window.bodymovin;
          if (player && typeof player.loadAnimation === 'function') resolve(player);
          else reject(new Error('Lottie player loaded without loadAnimation'));
        };
        script.onerror = function () { reject(new Error('Lottie player failed to load')); };
        document.head.appendChild(script);
      });
      return playerPromise;
    }

    function mountAnimation(wrap, media) {
      if (reducedMotion()) return;
      var animationUrl = assetUrl(media.animation);
      if (!animationUrl) return;
      var mount = el('div', 'gg-wz-media-animation');
      mount.setAttribute('aria-hidden', 'true');
      wrap.appendChild(mount);

      // Most Lotties stay on the player's native `path` loader. Apart from
      // avoiding an unnecessary second request, that is the most robust path
      // across Shopify's asset host aliases. Only a JSON with a declared
      // placeholder raster needs to be fetched first so that filename can be
      // replaced with the real Shopify poster URL (currently cp.success).
      var posterUrl = assetUrl(media.poster);
      var animationDataPromise = media.resolvePosterAsset
        ? fetch(animationUrl).then(function (res) {
          if (!res.ok) throw new Error('Animation JSON could not load (' + res.status + ')');
          return res.json();
        })
        : Promise.resolve(null);

      Promise.all([
        loadPlayer(),
        animationDataPromise
      ]).then(function (results) {
        var player = results[0];
        var animationData = results[1];
        if (posterUrl && animationData.assets) {
          animationData.assets.forEach(function (a) {
            if (a.p === 'IMAGE_PLACEHOLDER') {
              a.p = posterUrl;
              a.u = '';
              a.e = 0;
            }
          });
        }
        // The timer may have advanced while the tiny player was loading.
        if (!document.documentElement.contains(mount)) return;
        var options = {
          container: mount,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          rendererSettings: { preserveAspectRatio: 'xMidYMid meet' }
        };
        if (animationData) options.animationData = animationData;
        else options.path = animationUrl;
        var animation = player.loadAnimation(options);
        // Most instructional loops should keep playing, but a celebratory
        // animation can opt into ending after a short moment. It remains
        // visible on its final frame rather than disappearing with the timer.
        var stopAfter = Number(media.stopAfter);
        if (stopAfter > 0) {
          window.setTimeout(function () {
            if (mount.parentNode) animation.pause();
          }, stopAfter * 1000);
        }
        // The poster is the intentional first paint and failure fallback; it
        // must not remain visibly stacked under a successfully-rendered SVG
        // animation. Keeping it in the DOM preserves the fallback without
        // making the live motion look like it has a second frozen frame.
        animation.addEventListener('DOMLoaded', function () {
          if (document.documentElement.contains(mount)) wrap.classList.add('gg-wz-media--playing');
        });
        animation.addEventListener('data_failed', function () {
          animation.destroy();
          wrap.classList.remove('gg-wz-media--playing');
          mount.remove();
        });
      }).catch(function (err) {
        // Poster stays in place. Motion is progressive enhancement, not a
        // reason to interrupt molding, but leave a useful console trace for
        // the live acceptance pass instead of silently masking a bad asset.
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn('[GG Wizard] Animation fallback:', media.animation, err);
        }
        if (mount.parentNode) mount.remove();
      });
    }

    function diagnosticGrid(media) {
      var grid = el('div', 'gg-wz-media gg-wz-media--diagnostics');
      grid.setAttribute('role', 'list');
      grid.setAttribute('aria-label', media.label || 'Reference mold outcomes');
      (media.items || []).forEach(function (item) {
        var src = assetUrl(item.asset);
        if (!src) return;
        var figure = el('figure', 'gg-wz-diagnostic');
        figure.setAttribute('role', 'listitem');
        figure.appendChild(image(src, item.alt, 'gg-wz-diagnostic-image'));
        if (item.label) figure.appendChild(el('figcaption', 'gg-wz-diagnostic-label', item.label));
        grid.appendChild(figure);
      });
      return grid.childElementCount ? grid : placeholder(media);
    }

    // `media.whiteBg: true` — opt-in per asset, not a global default. Most
    // of the new v2 illustrations aren't drawn full-bleed to their canvas,
    // so .gg-wz-media's usual grey (--gg-wz-surface-sunk) shows through as
    // an unwanted border around them — but that grey is still doing real
    // framing work on the diagnostic pickers (check.look/check.diagnose),
    // so this stays a per-screen flag in flow.json rather than a change to
    // the shared default.
    function build(media) {
      if (!media) return placeholder(media);
      if (media.type === 'diagnosticGrid') return diagnosticGrid(media);

      var src = assetUrl(media.asset);
      if (!src && !media.animation) return placeholder(media);

      var wrap = el('div', 'gg-wz-media' + (media.animation ? ' gg-wz-media--animation' : ' gg-wz-media--image') + (media.whiteBg ? ' gg-wz-media--white' : ''));
      wrap.setAttribute('data-media-key', media.animation || media.asset || 'asset');
      // The V2 illustration delivery is 1000×650. Preserve that horizontal
      // canvas for its mapped assets instead of placing them in the V1 square
      // media box and wasting vertical wizard space. Explicit `aspect` still
      // wins for any future asset with a different art direction.
      var fallbackAspect = media.animation ? '1 / 1' : (/V2$/.test(media.asset || '') ? '20 / 13' : '1 / 1');
      setAspect(wrap, media, fallbackAspect);

      if (media.animation) {
        var poster = assetUrl(media.poster);
        if (!poster) return placeholder(media);
        wrap.appendChild(image(poster, media.alt, 'gg-wz-media-poster'));
        mountAnimation(wrap, media);
      } else {
        var img = image(src, media.alt, 'gg-wz-media-image');
        img.addEventListener('error', function () {
          var fallback = placeholder(media);
          if (wrap.parentNode) wrap.parentNode.replaceChild(fallback, wrap);
        });
        wrap.appendChild(img);
      }
      return wrap;
    }

    function replace(current, media) {
      if (!current || !current.parentNode) return current;
      var next = build(media);
      current.parentNode.replaceChild(next, current);
      return next;
    }

    return { build: build, replace: replace };
  })();

  /**
   * The one-time confetti burst for cp.success — separate from the Lottie
   * in Media, which loops continuously and is deliberately ambient (see
   * build-success.js). This is the single dramatic entrance, and it only
   * ever fires once per saved session: gated by `step.celebrate` in
   * wizard-flow.json and the persisted `celebrated` flag in state.data,
   * both wired from Wizard.render's swap().
   *
   * Canvas + requestAnimationFrame, not CSS keyframes — revised per Jay
   * 16 Aug ("more of a confetti-explosion, canvas-confetti as inspo"). A
   * real explosion needs real physics (gravity accumulating, drag decaying
   * velocity, a tumble illusion from oscillating the squash on each
   * particle) that CSS keyframes can only crudely fake with fixed percentage
   * stops — canvas-confetti gets its quality from exactly this, and it's a
   * small enough recipe to own outright rather than pull in as a dependency,
   * so the project's no-CDN, no-third-party-runtime rule still holds.
   *
   * Origin is the on-screen centre of the Lottie's media box, not a fixed
   * point — per Jay, it should read as bursting out from behind the
   * pulsing glow, not raining from the top of the screen.
   */
  var Confetti = {
    burst: function (root) {
      if (reducedMotion()) return;
      var scope = root || document.body;

      var scopeBox = scope.getBoundingClientRect();
      var origin = { x: scopeBox.left + scopeBox.width / 2, y: scopeBox.top + scopeBox.height * 0.32 };
      var mediaEl = scope.querySelector('.gg-wz-media--animation, .gg-wz-media--image');
      if (mediaEl) {
        var mr = mediaEl.getBoundingClientRect();
        if (mr.width && mr.height) origin = { x: mr.left + mr.width / 2, y: mr.top + mr.height / 2 };
      }

      // Resolved computed colours, not the custom-property strings
      // themselves — canvas fillStyle doesn't evaluate var(...), so this
      // still follows whatever the merchant has configured for
      // --gg-wz-blue/navy/success rather than a hardcoded palette.
      var rootStyle = getComputedStyle(scope);
      var colors = [
        rootStyle.getPropertyValue('--gg-wz-blue').trim() || '#5271ff',
        rootStyle.getPropertyValue('--gg-wz-navy').trim() || '#02084b',
        rootStyle.getPropertyValue('--gg-wz-success').trim() || '#0b6b32',
        '#ffffff'
      ];

      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var canvas = document.createElement('canvas');
      canvas.className = 'gg-wz-confetti';
      canvas.setAttribute('aria-hidden', 'true');
      scope.appendChild(canvas);
      // Sized from the canvas's own laid-out box, not window.innerWidth —
      // `position: fixed; inset: 0` (see wizard.css) already makes that box
      // exactly the viewport, and reading it back this way sidesteps every
      // window/documentElement viewport-size inconsistency across embedding
      // contexts rather than trusting any one of them.
      var box = canvas.getBoundingClientRect();
      var pageW = box.width || window.innerWidth || 320;
      var pageH = box.height || window.innerHeight || 480;
      canvas.width = pageW * dpr;
      canvas.height = pageH * dpr;
      var ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);

      // Tuned against a real mobile viewport (~800px tall), not desktop —
      // this is a mobile-only full-viewport app. The original 0.32/13
      // pairing was borrowed from a generic desktop-sized recipe and made
      // the burst fall out of frame in ~1.2s on a phone-height screen,
      // barely a third of the intended 2.6s+0.7s hang-and-fade.
      var GRAVITY = 0.065;
      var DRAG = 0.985;
      var DURATION = 2600; // ms of active physics before the fade-out window
      var FADE = 700; // ms tail after DURATION where survivors dim out

      var count = 130;
      var particles = [];
      for (var i = 0; i < count; i++) {
        // Full 360° so it reads as an explosion radiating out from the
        // glow, not a cannon shooting one direction; the small upward vy
        // bias below is what keeps it from looking like an even sphere.
        var angle = Math.random() * Math.PI * 2;
        var speed = 2 + Math.random() * 6;
        particles.push({
          x: origin.x,
          y: origin.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 2,
          size: 5 + Math.random() * 6,
          color: colors[(Math.random() * colors.length) | 0],
          round: Math.random() < 0.35,
          rotation: Math.random() * Math.PI,
          spin: (Math.random() * 2 - 1) * 0.3,
          tilt: Math.random() * Math.PI * 2,
          tiltSpin: 0.15 + Math.random() * 0.25
        });
      }

      var start = null;
      var lastTs = null;

      function frame(ts) {
        if (start === null) start = ts;
        if (lastTs === null) lastTs = ts;
        // Physics scaled to elapsed real time, not a flat per-call step —
        // GRAVITY/DRAG below are tuned assuming 60fps (dt=1 at a 16.7ms
        // frame); without this, a display running faster or slower than
        // that would make the whole burst fall proportionally faster or
        // slower than intended, which is exactly what happened testing
        // this in a harness whose rAF doesn't throttle to a real refresh
        // rate the way an actual browser tab does.
        var dt = Math.min((ts - lastTs) / (1000 / 60), 4);
        lastTs = ts;
        var elapsed = ts - start;
        ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

        var alpha = elapsed <= DURATION ? 1 : Math.max(0, 1 - (elapsed - DURATION) / FADE);
        var anyVisible = false;

        for (var j = 0; j < particles.length; j++) {
          var p = particles[j];
          var dragFactor = Math.pow(DRAG, dt);
          p.vx *= dragFactor;
          p.vy = p.vy * dragFactor + GRAVITY * dt;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.rotation += p.spin * dt;
          p.tilt += p.tiltSpin * dt;

          if (p.y - p.size > pageH || alpha <= 0) continue;
          anyVisible = true;

          // Squashing height by |cos(tilt)| as it spins is the whole trick
          // behind canvas-confetti's "tumbling card" look — a flat 2D
          // rotation alone reads as a spinning coin, not falling paper.
          var squash = Math.abs(Math.cos(p.tilt));
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.globalAlpha = alpha;
          ctx.fillStyle = p.color;
          if (p.round) {
            ctx.beginPath();
            ctx.ellipse(0, 0, p.size / 2, (p.size / 2) * Math.max(squash, 0.15), 0, 0, Math.PI * 2);
            ctx.fill();
          } else {
            var h = Math.max(p.size * 0.42 * squash, 0.6);
            ctx.fillRect(-p.size / 2, -h / 2, p.size, h);
          }
          ctx.restore();
        }

        if (anyVisible && elapsed < DURATION + FADE) {
          requestAnimationFrame(frame);
        } else if (canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      }

      requestAnimationFrame(frame);
    }
  };

  /**
   * Builds a single action button from a step + action definition. Shared
   * between Wizard.buildActions (the bottom action bar) and Renderer.build's
   * `step.inlineAction` (a button rendered in the main content flow instead
   * — care.review's "Write a review" needed to sit right next to the review
   * copy, with its own "opens in a new tab" caption directly underneath,
   * rather than in the bar alongside "Continue"). Same action schema either
   * way: visibleIf/hrefFromConfig/reviewLink/overlay/set/increment/next all
   * behave identically regardless of where the resulting button ends up.
   *
   * `hrefFromConfig` resolves an action to a merchant-configured URL
   * (care.more's ebook/app links) instead of an in-flow `next` — an
   * outbound link, opened in a new tab so the wizard itself is never
   * lost. Per CUSTOMER_JOURNEY.md §10, the app link is "a config slot,
   * hidden until populated" — returning null here (rather than a
   * disabled or dead button) is what makes that literal: an unset
   * setting means the action doesn't exist, not that it's greyed out.
   */
  function buildActionButton(step, action) {
    // `visibleIf` — the triage "use your spare" offers, gated on actual
    // inventory (`guards.{size}` — see resolvePathTokens). Per
    // CUSTOMER_JOURNEY.md §7, an empty inventory means the option isn't
    // offered at all, not shown-then-disabled ("the wizard says so
    // plainly rather than offering a spare that doesn't exist").
    if (action.visibleIf && !evalCondition(action.visibleIf)) return null;

    // Resolved once — labels can carry the same {token}s as body copy
    // (triage.spent's "Use my other {sizeLabel}" needs this to actually
    // say "Small"/"Large" rather than the literal token).
    var label = interpolate(action.label);

    if (action.hrefFromConfig) {
      var url = config[action.hrefFromConfig];
      if (!url) return null;
      var a = el('a', 'gg-wz-btn gg-wz-btn--' + (action.style || 'secondary'), label);
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.addEventListener('click', function () {
        track('gg_wizard_action', { wizard_step: step.__id, label: label });
      });
      return a;
    }

    // `reviewLink: true` — care.review's "Write a review". A plain new
    // tab, not `Overlay.openIframe`: confirmed live that judge.me's own
    // domain sends `X-Frame-Options: sameorigin`, which no code on our
    // side can override — it's enforced by the browser against their
    // response header, the same category of restriction as CORS. A
    // blocked frame also renders blank with no error the parent page
    // can see, so "try the frame, fall back on failure" was never
    // reliably buildable for an arbitrary merchant-configured URL. A
    // real `target="_blank"` link sidesteps the whole problem — framing
    // restrictions don't apply to a full top-level navigation in a
    // separate tab, so this is guaranteed to work regardless of what
    // headers the destination sends. No `config.reviews.url` configured
    // means there's nowhere to send them — same "don't offer it at all"
    // pattern as hrefFromConfig above, not a dead button.
    if (action.reviewLink) {
      var reviewUrl = config.reviews && config.reviews.url;
      if (!reviewUrl) return null;
      var rlBtn = el('a', 'gg-wz-btn gg-wz-btn--' + (action.style || 'primary'), label);
      rlBtn.href = reviewUrl;
      rlBtn.target = '_blank';
      rlBtn.rel = 'noopener';
      rlBtn.addEventListener('click', function () {
        track('gg_wizard_action', { wizard_step: step.__id, label: label });
      });
      return rlBtn;
    }

    // `overlay: "help" | "replacement"` — the triage "talk to someone"
    // and "get my free replacement" actions. Per CUSTOMER_JOURNEY.md §8
    // this is an overlay that dismisses back to wherever it was opened
    // from, not a flow-graph destination — so this never navigates,
    // unlike every other action. "help" pre-fills the issue field from
    // the current diagnosis (if any) and requires a photo whenever
    // there's a diagnosis to back it up; "replacement" always requires
    // one (CANONICAL_FACTS.md) and pre-fills the size already in use.
    if (action.overlay) {
      var ovBtn = el('button', 'gg-wz-btn gg-wz-btn--' + (action.style || 'text') + (action.compact ? ' gg-wz-btn--compact' : ''), label);
      ovBtn.type = 'button';
      ovBtn.addEventListener('click', function () {
        var opts = {};
        if (action.overlay === 'help') {
          var diag = State.get('diagnosis', '');
          opts.presetIssue = interpolate(DIAGNOSIS_LABELS[diag] || '');
          opts.photoRequired = Boolean(diag);
        } else if (action.overlay === 'replacement') {
          opts.presetSize = State.get('size', STATE_UNSET_DEFAULTS.size);
          opts.photoRequired = true;
        } else if (action.overlay === 'info') {
          opts.title = action.infoTitle;
          opts.body = action.infoBody;
        }
        // JSON-level overrides — care.feedback's "tell us what happened"
        // isn't diagnosis-driven (a low sentiment score on an otherwise
        // fine mold has no `state.diagnosis` to derive a prefill from),
        // so it sets its own instead of relying on the diagnosis lookup.
        if (action.presetIssue) opts.presetIssue = interpolate(action.presetIssue);
        if (action.photoRequired != null) opts.photoRequired = Boolean(action.photoRequired);
        if (action.prompts) opts.prompts = action.prompts;
        if (action.onSuccessNext) opts.onSuccessNext = action.onSuccessNext;
        Overlay.open(action.overlay, opts);
      });
      return ovBtn;
    }

    var btn = el('button', 'gg-wz-btn gg-wz-btn--' + (action.style || 'primary') + (action.compact ? ' gg-wz-btn--compact' : ''), label);
    btn.type = 'button';
    btn.addEventListener('click', function () {
      // No tap-confirmation cue — see the matching note in buildChoices.
      if (action.set) State.set(action.set);
      // `increment` — deltas (attempt counts, guard inventory, the
      // heat-time bias a triage remedy nudges), as opposed to `set`'s
      // static overwrite. Keys may carry a `{token}` (e.g.
      // "guards.{size}") resolved against current state before applying.
      if (action.increment) {
        for (var k in action.increment) {
          if (Object.prototype.hasOwnProperty.call(action.increment, k)) {
            State.increment(resolvePathTokens(k), action.increment[k]);
          }
        }
      }
      track('gg_wizard_action', { wizard_step: step.__id, label: label });
      Wizard.advance(action.next);
    });
    return btn;
  }

  /**
   * Shared between the bottom action bar (Renderer.buildActions) and the
   * auto-inline layout below — a step's Back target always behaves the same
   * way regardless of where the resulting link ends up: pop history rather
   * than push a new entry, which is why this calls Wizard.advance directly
   * with `isBack:true` instead of going through buildActionButton's `next`
   * handling.
   */
  function buildBackButton(step, compact) {
    var backBtn = el(
      'button',
      compact ? 'gg-wz-btn gg-wz-btn--back' : 'gg-wz-btn gg-wz-btn--text',
      compact
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>'
        : '← Back'
    );
    backBtn.type = 'button';
    if (compact) backBtn.setAttribute('aria-label', 'Back');
    backBtn.addEventListener('click', function () { Wizard.advance(step.back, { isBack: true }); });
    return backBtn;
  }

  /**
   * True when a step's one real action should render inline at the end of
   * its content instead of pinned to the bottom bar — "Signs you've nailed
   * it", cp.success, cp.complete, and the single-action triage/education
   * screens all read better with Continue sitting right after what it's
   * responding to, rather than anchored below unrelated scroll. Applies as
   * a general rule (any qualifying content/checkpoint step) rather than a
   * hand-maintained list of step ids, so it doesn't rot as steps are added.
   *
   * Deliberately excludes anything with more than one *currently visible*
   * action (visibleIf resolved) — genuine multi-way branches like
   * triage.destroyed/bitthrough/spent/size_switch_large/size_switch_small
   * need their real choices weighted equally, not one promoted above the
   * fold. Also backs off entirely when a step already declares its own
   * inlineAction(s) (care.review, care.more, etc.) — those are bespoke
   * placements, not this rule.
   */
  function shouldAutoInline(step) {
    if (step.inlineAction || step.inlineActions) return false;
    if (step.type !== 'content' && step.type !== 'checkpoint') return false;
    var actions = step.actions || [];
    if (!actions.length) return false;
    var visible = 0;
    for (var i = 0; i < actions.length; i++) {
      if (!actions[i].visibleIf || evalCondition(actions[i].visibleIf)) visible++;
      if (visible > 1) return false;
    }
    return visible === 1;
  }

  var Renderer = {
    build: function (step) {
      // A real DocumentFragment, not a wrapping <div> — its children become
      // direct children of .gg-wz-step once appended (fragments always
      // flatten on insertion), which is what every ".gg-wz-step > X"
      // spacing rule in wizard.css assumes. This used to be
      // `document.createElement('div')`, which silently interposed an
      // unnamed wrapper div between .gg-wz-step and everything in it —
      // every child-combinator spacing rule this session was quietly
      // failing to match anything as a result, even though several of
      // them appeared to work in ad-hoc testing (margin collapse and
      // pre-existing rules elsewhere approximated the right spacing by
      // coincidence often enough to mask it).
      var frag = document.createDocumentFragment();

      // moldStage steps (mold.pour_cool/heat/shape/cool) keep only a
      // centred eyebrow above the countdown — no title, no body. The
      // coaching list's own label/detail text carries what a title+body
      // would otherwise say, and the mold header (Wizard.renderMoldChrome)
      // already tells you which of the four stages you're in, so a title
      // repeating that would be redundant. Per the agreed worked example:
      // eyebrow → countdown → list → control, nothing else.
      if (step.eyebrow) {
        frag.appendChild(el('p', 'gg-wz-eyebrow' + (step.moldStage ? ' gg-wz-eyebrow--center' : ''), interpolate(step.eyebrow)));
      }

      if (step.title && !step.moldStage) frag.appendChild(el('h1', 'gg-wz-title', interpolate(step.title)));

      if (step.body && !step.moldStage) frag.appendChild(el('p', 'gg-wz-body-text', interpolate(step.body)));

      if (step.media) frag.appendChild(this.buildMedia(step.media));

      if (step.panel) frag.appendChild(this.buildPanel(step.panel));

      if (step.callouts) {
        step.callouts.forEach(function (callout) {
          if (callout.visibleIf && !evalCondition(callout.visibleIf)) return;
          frag.appendChild(Renderer.buildPanel(callout, true));
        });
      }

      if (step.timeline) frag.appendChild(this.buildTimeline(step.timeline));

      // Mount point only — ReviewAdapter fills it in once the step is
      // actually in the DOM (see the wireReviewWidget call in Wizard.render).
      if (step.reviewWidget) frag.appendChild(el('div', 'gg-wz-review-mount'));

      // Actions rendered in the main content flow instead of the bottom
      // bar — care.review's "Write a review" needed to sit right next to
      // the review copy, with its own caption directly underneath ("Opens
      // in a new tab — come back here when you're done"), rather than
      // compete with "Continue" down in the bar. Same action schema as
      // step.actions (buildActionButton is shared), so each one can carry
      // visibleIf/reviewLink/overlay/hrefFromConfig/next/etc exactly the
      // same way. `inlineActions` (plural, an array) is the general form —
      // e.g. care.more's ebook link followed by its own "Continue" at the
      // true end of the content, per the read-heavy-page pattern below.
      // `inlineAction` (singular) still works as shorthand for a one-item
      // array, for every step that only ever needed the one.
      var inlineActionList = step.inlineActions || (step.inlineAction ? [step.inlineAction] : []);
      inlineActionList.forEach(function (action) {
        var inlineBtn = buildActionButton(step, action);
        if (inlineBtn) {
          var inlineWrap = el('div', 'gg-wz-inline-action' + (action.compact ? ' gg-wz-inline-action--compact' : ''));
          inlineWrap.appendChild(inlineBtn);
          if (action.caption) {
            inlineWrap.appendChild(el('p', 'gg-wz-inline-action-caption', interpolate(action.caption)));
          }
          frag.appendChild(inlineWrap);
        }
      });

      // Auto-inline: a qualifying step's one real action (see
      // shouldAutoInline) moves down here instead of sitting in the bottom
      // bar — Renderer.buildActions backs off for the same steps so nothing
      // duplicates. Back, if present, becomes a plain text link underneath
      // rather than a compact paired button, since there's no primary
      // button left in the bar to pair it against.
      if (shouldAutoInline(step)) {
        var autoAction = null;
        for (var ai = 0; ai < step.actions.length; ai++) {
          if (!step.actions[ai].visibleIf || evalCondition(step.actions[ai].visibleIf)) {
            autoAction = step.actions[ai];
            break;
          }
        }
        var autoBtn = autoAction && buildActionButton(step, autoAction);
        if (autoBtn) {
          var autoWrap = el('div', 'gg-wz-inline-action');
          autoWrap.appendChild(autoBtn);
          if (autoAction.caption) {
            autoWrap.appendChild(el('p', 'gg-wz-inline-action-caption', interpolate(autoAction.caption)));
          }
          if (step.back) autoWrap.appendChild(buildBackButton(step, false));
          frag.appendChild(autoWrap);
        }
      }

      if (step.type === 'choice') {
        frag.appendChild(step.choiceLayout === 'cards' ? this.buildChoiceCards(step) : this.buildChoices(step));
      }
      if (step.type === 'toggles') frag.appendChild(this.buildToggles(step));
      if (step.type === 'timer') frag.appendChild(this.buildTimer(step));

      return frag;
    },

    /**
     * Static shell only — numeral, progress bar, coaching line, restart
     * control. The live behaviour (starting/resuming the Timer, updating
     * these elements, enabling the primary action on completion) is wired
     * in Wizard.render's swap(), which is where the primary button — a
     * sibling in #gg-wz-actions, not a descendant of this markup — actually
     * lives. Same pattern as wireGate.
     */
    buildTimer: function (step) {
      var wrap = el('div', 'gg-wz-timer');
      wrap.appendChild(el('div', 'gg-wz-timer-value', ''));
      var track = el('div', 'gg-wz-timer-track');
      track.appendChild(el('div', 'gg-wz-timer-bar'));
      wrap.appendChild(track);
      // Lightweight "Next up" cue for non-focal chain.autoAdvance timer
      // transitions — see wireTimer and .gg-wz-timer-nextup in wizard.css.
      // Empty/hidden on every other timer step; only populated in the final
      // lead-in seconds of a step whose chain.autoAdvance isn't focal.
      wrap.appendChild(el('div', 'gg-wz-timer-nextup'));

      // A persistent checklist rather than one line that gets silently
      // replaced — real-device feedback was that a fast multi-stage
      // countdown (mold.shape's press → suck in → hold, each ~5s apart)
      // moved too quickly to read as a single changing sentence. Every
      // stage is visible throughout; the active one carries the fuller
      // instruction, done ones collapse to just their short label with a
      // check mark, upcoming ones sit dimmed. Degrades gracefully to what
      // reads as a single line when a timer has only one coaching entry
      // (mold.cool) — same markup, no special case needed.
      //
      // `visibleIf` on a coaching entry — e.g. mold.pour_cool's kettle-only
      // "Pour it in" vs. stovetop-only "Pour it in, carefully" lead-in stage
      // — same evalCondition already used by callouts/actions, filtered out
      // here so a hidden-for-this-heatMethod entry never appears as a
      // "todo" item in the initial checklist paint.
      var coaching = ((step.timer && step.timer.coaching) || []).filter(function (entry) {
        return !entry.visibleIf || evalCondition(entry.visibleIf);
      });
      if (coaching.length) {
        var list = el('ul', 'gg-wz-coach-list');
        coaching.forEach(function (entry, i) {
          var item = el(
            'li',
            'gg-wz-coach-item',
            '<span class="gg-wz-coach-mark" aria-hidden="true"></span>' +
            '<span class="gg-wz-coach-text">' +
            '<span class="gg-wz-coach-label">' + interpolate(entry.label || entry.text) + '</span>' +
            '<span class="gg-wz-coach-detail">' + interpolate(entry.text) + '</span>' +
            '</span>'
          );
          item.setAttribute('data-state', i === 0 ? 'active' : 'todo');
          list.appendChild(item);
        });
        wrap.appendChild(list);
      }

      if (!wakeLockCtl.supported) {
        wrap.appendChild(el('p', 'gg-wz-muted', '💡 Keep your screen on so the timer keeps running.'));
      }
      var extendBtn = el('button', 'gg-wz-btn gg-wz-btn--text gg-wz-timer-extend', 'Need more time? +5s');
      extendBtn.type = 'button';
      wrap.appendChild(extendBtn);

      // `notBoiledYet` — mold.pour_cool's own correction link (arrived at
      // this countdown before the water actually boiled). An in-flow fix,
      // not an exit, so it's available even though `noExit` suppresses
      // everything else that might invite leaving. Click handling lives in
      // wireTimer, where Timer is in scope.
      if (step.notBoiledYet) {
        var notBoiledBtn = el('button', 'gg-wz-btn gg-wz-btn--text gg-wz-timer-notboiled', "It's not boiled yet");
        notBoiledBtn.type = 'button';
        wrap.appendChild(notBoiledBtn);
      }

      return wrap;
    },

    buildMedia: function (media) {
      return Media.build(media);
    },

    buildPanel: function (panel, isCallout) {
      var wrap = el('section', 'gg-wz-panel' + (panel.style ? ' gg-wz-panel--' + panel.style : '') + (isCallout ? ' gg-wz-callout' : ''));
      if (panel.title) wrap.appendChild(el('h2', 'gg-wz-panel-title', interpolate(panel.title)));
      // Optional small reference image above the list — e.g. the trim
      // steps' "1–2mm at a time" bullet paired with an actual scale
      // illustration, rather than just describing it in words.
      if (panel.media) wrap.appendChild(this.buildMedia(panel.media));
      var list = el('ul', 'gg-wz-list');
      (panel.items || []).forEach(function (item) {
        list.appendChild(el('li', null, interpolate(item)));
      });
      wrap.appendChild(list);
      return wrap;
    },

    buildTimeline: function (timeline) {
      var wrap = el('section', 'gg-wz-timeline' + (timeline.style === 'numbered' ? ' gg-wz-timeline--numbered' : ''));
      if (timeline.label) wrap.appendChild(el('p', 'gg-wz-timeline-label', interpolate(timeline.label)));
      var list = el('ol', 'gg-wz-timeline-list');
      (timeline.items || []).forEach(function (item) {
        var entry = el('li', 'gg-wz-timeline-item');
        entry.appendChild(el('h2', 'gg-wz-timeline-title', interpolate(item.title)));
        entry.appendChild(el('p', 'gg-wz-timeline-text', interpolate(item.text)));
        list.appendChild(entry);
      });
      wrap.appendChild(list);
      return wrap;
    },

    buildChoices: function (step) {
      var wrap = el('div', 'gg-wz-choices');
      (step.choices || []).forEach(function (choice) {
        // `visibleIf` — same convention as action.visibleIf (see
        // buildActionButton): an option that doesn't apply isn't offered at
        // all, not shown-then-disabled. First real use: triage.loose's
        // "try Small instead," which only makes sense for a Large guard.
        if (choice.visibleIf && !evalCondition(choice.visibleIf)) return;
        var btn = el(
          'button',
          'gg-wz-choice',
          '<span><span class="gg-wz-choice-label">' + interpolate(choice.label) + '</span>' +
          (choice.note ? '<span class="gg-wz-choice-note">' + interpolate(choice.note) + '</span>' : '') +
          '</span>' +
          (choice.badge ? '<span class="gg-wz-choice-badge">' + interpolate(choice.badge) + '</span>' : '')
        );
        btn.type = 'button';
        btn.setAttribute('data-choice-id', choice.id || choice.label);
        btn.addEventListener('click', function () {
          // No tap-confirmation cue here on purpose — sound/haptics are
          // reserved for the timed steps where they're actually load-bearing
          // (Phase 3). A chime on every navigation tap is what makes people
          // reach to mute an app in the first place, and it'd be genuinely
          // annoying with the phone sitting on a benchtop.
          if (choice.set) State.set(choice.set);
          track('gg_wizard_choice', { wizard_step: step.__id, value: choice.id });
          Wizard.advance(choice.next);
        });
        wrap.appendChild(btn);
      });
      return wrap;
    },

    /**
     * Image-led alternative to buildChoices — one self-contained card per
     * option (reference image, title, subtitle, its own "pick this" button)
     * instead of a decorative image grid decoupled from a separate text
     * list. Built for check.diagnose, where matching "which of these six
     * photos looks like mine" against a same-order-but-separate text list
     * was real friction the image-grid layout never solved. `cardColumns`
     * defaults to 1 (full-width, per real-device feedback that 2-up made
     * the reference image too small to read) but the CSS modifier for 2
     * already exists — see .gg-wz-choice-cards--cols-2 — so a future flip
     * is a JSON change, not a rebuild.
     */
    buildChoiceCards: function (step) {
      var cols = step.cardColumns || 1;
      var outer = document.createDocumentFragment();
      var wrap = el('div', 'gg-wz-choice-cards gg-wz-choice-cards--cols-' + cols);
      (step.choices || []).forEach(function (choice) {
        var card = el('article', 'gg-wz-choice-card');

        if (choice.media) {
          var mediaWrap = el('div', 'gg-wz-choice-card-media');
          mediaWrap.appendChild(Media.build(choice.media));
          card.appendChild(mediaWrap);
        }

        var body = el('div', 'gg-wz-choice-card-body');
        if (choice.title) body.appendChild(el('h3', 'gg-wz-choice-card-title', interpolate(choice.title)));
        if (choice.subtitle) body.appendChild(el('p', 'gg-wz-choice-card-subtitle', interpolate(choice.subtitle)));
        if (choice.tip) {
          body.appendChild(el('p', 'gg-wz-choice-card-tip',
            '<strong>Quick check:</strong> ' + interpolate(choice.tip)
          ));
        }
        card.appendChild(body);

        var btn = el('button', 'gg-wz-btn gg-wz-btn--secondary gg-wz-choice-card-btn',
          interpolate(choice.buttonLabel || step.cardButtonLabel || 'Select this one'));
        btn.type = 'button';
        btn.setAttribute('data-choice-id', choice.id || choice.title);
        btn.addEventListener('click', function () {
          if (choice.set) State.set(choice.set);
          track('gg_wizard_choice', { wizard_step: step.__id, value: choice.id });
          Wizard.advance(choice.next);
        });
        card.appendChild(btn);

        wrap.appendChild(card);
      });

      if (step.choiceHint) {
        var hint = el('button', 'gg-wz-choice-cards-hint',
          '<span>' + interpolate(step.choiceHint) + '</span>' +
          '<svg class="gg-wz-choice-cards-hint-chevron" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">' +
          '<path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        );
        hint.type = 'button';
        hint.addEventListener('click', function () {
          var firstCard = wrap.firstElementChild;
          if (firstCard) firstCard.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        });
        outer.appendChild(hint);
        // Deliberately no wireScrollFadeHint here, unlike the generic
        // floating overflow arrow — per Jay, this bar should never dismiss
        // itself or be remembered as seen, on this render or any later
        // revisit. It's a standing "here's how to read this screen" label,
        // not a one-time nudge.
      }

      outer.appendChild(wrap);
      return outer;
    },

    /**
     * Handles both "switches" (prep.experience — sound/haptic) and
     * "checkboxes" (prep.safety, prep.gather) — same data model (a set of
     * on/off items), different visual affordance chosen per the semantics of
     * the question. Using the switch/speaker visual for a medical question
     * read as confusing on a real device, so the visual is purely a function
     * of `step.style`, never hardcoded to what it's "usually" used for.
     *
     * `store: "prefs"` writes straight to the prefs object (sound/haptic);
     * `store: "flags"` collects the ids of the currently-true items into a
     * named array on state.data.
     *
     * `item.capability` optionally names a browser feature the item depends
     * on (currently only "vibrate"); unsupported items are omitted rather
     * than shown disabled, since iOS Safari has no Vibration API at all and
     * a permanently-dead toggle is worse than not offering it.
     */
    buildToggles: function (step) {
      var container = el('div', 'gg-wz-choices');
      var isSwitch = step.style === 'switches';

      var current = step.store === 'flags' ? (State.get(step.field, []) || []).slice() : null;

      var items = (step.items || []).filter(function (item) {
        return item.capability !== 'vibrate' || 'vibrate' in navigator;
      });

      // First-ever visit to a prefs-store step (sound/haptic): seed from
      // each item's own schema `default` rather than leaving prefs at their
      // neutral off-by-default state. Reads as "on, as a suggestion — turn
      // it off if you'd rather", not "this app has sound now." `asked`
      // guards this so a later revisit (e.g. via Back) shows whatever the
      // customer actually chose, not the defaults again.
      if (step.store === 'prefs' && !prefs.asked) {
        items.forEach(function (item) { prefs[item.id] = Boolean(item.default); });
        prefs.asked = true;
        savePrefs();
        syncHeaderMute();
      }

      var hintEl = null;
      if (step.offHint) {
        hintEl = el('div', 'gg-wz-panel gg-wz-panel--warning gg-wz-hint', step.offHint.message);
      }

      // Sits above the checklist, not below it — a plain muted line under a
      // multi-item list read as an afterthought most people never scrolled
      // down far enough to see, right when it's the one thing that explains
      // why the primary button won't respond yet. `step.listHint` is the
      // same slot for a non-gated toggles step (e.g. prep.safety) that just
      // wants a short "here's what this list is for" line above the items,
      // with no Continue-blocking behaviour attached.
      var gateHintEl = null;
      if (step.gate === 'allChecked') {
        gateHintEl = el('div', 'gg-wz-panel', 'Tick off the required items below to continue. Optional ones will not hold you up.');
      } else if (step.listHint) {
        gateHintEl = el('div', 'gg-wz-panel', interpolate(step.listHint));
      }

      function updateHint() {
        if (!hintEl || !step.offHint) return;
        var isOff = step.store === 'prefs'
          ? !prefs[step.offHint.itemId]
          : current.indexOf(step.offHint.itemId) === -1;
        hintEl.style.display = isOff ? '' : 'none';
      }

      items.forEach(function (item) {
        var initial = step.store === 'prefs'
          ? Boolean(prefs[item.id])
          : current.indexOf(item.id) !== -1 || Boolean(item.default);

        var visual = isSwitch
          ? '<span class="gg-wz-switch" aria-hidden="true"></span>'
          : '<span class="gg-wz-checkbox" aria-hidden="true">' +
            '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8.5 6.5 12 13 4"/></svg></span>';

        // `item.onNote` — a second note line shown only while this specific
        // item is toggled on, right on the row itself (not a page-level
        // panel like offHint/gateHintEl above). Added for prep.experience's
        // "make sure your phone's on loud" reminder — a page-level panel
        // for it sat below the toggle list, under the fixed action bar on
        // real devices, easy to miss entirely. Anchored to the row instead,
        // it's impossible to scroll past without also seeing the switch
        // it's talking about.
        var row = el(
          'button',
          'gg-wz-choice gg-wz-toggle-row',
          (item.badge ? '<span class="gg-wz-choice-badge">' + interpolate(item.badge) + '</span>' : '') +
          '<span><span class="gg-wz-choice-label">' + item.label + '</span>' +
          (item.note ? '<span class="gg-wz-choice-note">' + interpolate(item.note) + '</span>' : '') +
          (item.onNote ? '<span class="gg-wz-choice-note gg-wz-choice-note--on"' + (initial ? '' : ' style="display:none"') + '>' + interpolate(item.onNote) + '</span>' : '') +
          '</span>' + visual
        );
        row.type = 'button';
        row.setAttribute('aria-pressed', String(initial));
        row.setAttribute('data-optional', String(Boolean(item.optional)));

        var onNoteEl = item.onNote ? row.querySelector('.gg-wz-choice-note--on') : null;

        row.addEventListener('click', function () {
          var on = row.getAttribute('aria-pressed') !== 'true';
          row.setAttribute('aria-pressed', String(on));
          if (onNoteEl) onNoteEl.style.display = on ? '' : 'none';

          if (step.store === 'prefs') {
            prefs[item.id] = on;
            prefs.asked = true;
            savePrefs();
            syncHeaderMute();
            if (on && item.id === 'sound') { cues.beep(880, 120); }
            if (on && item.id === 'haptic') { cues.buzz(30); }
          } else {
            var idx = current.indexOf(item.id);
            if (on && idx === -1) current.push(item.id);
            if (!on && idx !== -1) current.splice(idx, 1);
            var patch = {};
            patch[step.field] = current.slice();
            State.set(patch);
          }

          updateHint();
        });

        container.appendChild(row);
      });

      if (!hintEl && !gateHintEl) return container;

      updateHint();
      var wrap = document.createDocumentFragment();
      if (gateHintEl) wrap.appendChild(gateHintEl);
      wrap.appendChild(container);
      if (hintEl) wrap.appendChild(hintEl);
      return wrap;
    },

    /**
     * Layout rule: when a step has a back target AND exactly one primary
     * action, they share a row — a compact back-arrow square (~20% width)
     * beside a full-width primary button — rather than each stacking as its
     * own full-height row. Cut the action bar's height substantially on a
     * real device, where every extra centimetre is content the customer
     * can't see without scrolling. Any additional (secondary/text) actions
     * still stack beneath the row, so multi-option screens like
     * prep.safety_gate keep working.
     *
     * Pure-selection steps (choice/toggles with no explicit actions) get
     * just a small back link — there's no "continue" button because the
     * choice itself *is* the continue.
     */
    buildActions: function (step) {
      var wrap = document.createDocumentFragment();

      // Auto-inlined steps (see shouldAutoInline) render their one action —
      // and their Back link, if any — down in the content flow instead
      // (Renderer.build). Nothing left for the bottom bar to show; leaving
      // it empty is what lets .gg-wz-actions--empty collapse it entirely.
      if (shouldAutoInline(step)) return wrap;

      var actions = step.actions || [];

      // Button construction itself lives in the shared buildActionButton —
      // see its doc comment for the full action schema. This closure only
      // adds the bottom-bar-specific bit: which button gets primary billing.
      function makeButton(action) { return buildActionButton(step, action); }
      function makeBackButton(compact) { return buildBackButton(step, compact); }

      if (actions.length === 0) {
        if (step.back) wrap.appendChild(makeBackButton(false));
        return wrap;
      }

      // The first action gets primary billing (paired with Back if there is
      // one) — but if it's config-gated and unset, fall through to the next
      // real action rather than leaving an empty primary slot.
      var firstBtn = null;
      var firstIndex = 0;
      for (; firstIndex < actions.length; firstIndex++) {
        firstBtn = makeButton(actions[firstIndex]);
        if (firstBtn) break;
      }

      if (firstBtn) {
        if (step.back) {
          var row = el('div', 'gg-wz-actions-row');
          row.appendChild(makeBackButton(true));
          row.appendChild(firstBtn);
          wrap.appendChild(row);
        } else {
          wrap.appendChild(firstBtn);
        }
      } else if (step.back) {
        wrap.appendChild(makeBackButton(false));
      }

      actions.slice(firstIndex + 1).forEach(function (action) {
        var btn = makeButton(action);
        if (btn) wrap.appendChild(btn);
      });

      return wrap;
    }
  };

  // ------------------------------------------------------------------
  // Wizard controller
  // ------------------------------------------------------------------

  /**
   * Transition timing, read once from the config the section schema writes.
   * Split into an exit share and a longer enter share — a quick exit reads
   * as responsive, a slightly more settled enter reads as considered, and
   * together they're deliberately longer than Phase 2's original ~260ms,
   * which read as an instant snap on a real device rather than a transition
   * at all. `prefers-reduced-motion` and `data-transition="none"` collapse
   * both to 0.
   */
  var TRANSITION_TOTAL_MS = (config.transition && config.transition.duration) || 420;

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function transitionBudget() {
    var mode = (config.transition && config.transition.type) || 'slide';
    if (mode === 'none' || reducedMotion()) return { exit: 0, enter: 0 };
    var total = TRANSITION_TOTAL_MS;
    return { exit: Math.round(total * 0.4), enter: Math.round(total * 0.6) };
  }

  /**
   * The little "+5" that jumps up next to a countdown and fades, confirming
   * a buffer nudge actually happened — shared by the in-timer +5s control
   * and the "up next" banner's +5s control, since both are the same
   * gesture (delay this deadline a bit) in two different places.
   */
  function showPlusFiveBadge(anchorEl) {
    if (!anchorEl) return;
    var parent = anchorEl.parentNode;
    if (!parent) return;
    if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
    var badge = el('span', 'gg-wz-plus-five', '+5');
    badge.setAttribute('aria-hidden', 'true');
    parent.appendChild(badge);
    badge.addEventListener('animationend', function () { badge.remove(); });
    // Belt-and-braces cleanup — if the step navigates away mid-animation,
    // animationend may never fire on a now-detached element.
    setTimeout(function () { if (badge.parentNode) badge.remove(); }, 1200);
  }

  // A disabled-but-visible primary button during a timer/priming run sat in
  // the action bar for the full countdown (up to 2 minutes on mold.pour_cool)
  // doing nothing but taking up space that should belong to the coaching
  // content above it. Hidden outright instead, matching how
  // beginChainAdvance already hides it once a chain banner takes over —
  // this just applies the same treatment to the timer-running state that
  // precedes it, not only the auto-advance state that follows it.
  function setPrimaryHidden(btn, hidden) {
    if (!btn) return;
    btn.disabled = hidden;
    btn.style.display = hidden ? 'none' : '';
    // Hiding the button removes it from the layout, but #gg-wz-actions
    // itself still carries its own padding/border — an empty bar still
    // ate a visible strip at the bottom of the screen during a touchless
    // run, taking space that belongs to the coaching content. Collapse
    // the bar itself whenever nothing inside it is actually visible
    // (checked generically, not assumed, since a step can also have a
    // back button living alongside the primary).
    var bar = btn.closest('.gg-wz-actions');
    if (!bar) return;
    var hasVisible = Array.prototype.some.call(bar.children, function (c) {
      return getComputedStyle(c).display !== 'none';
    });
    bar.classList.toggle('gg-wz-actions--empty', !hasVisible);
  }

  /**
   * "Scroll to find yours" only needs saying once — a scroll-down hint
   * fades away (and collapses out of layout) the moment the customer
   * actually scrolls, and stays gone for that step for the rest of this
   * browser session (sessionStorage, so a genuinely fresh session sees it
   * again). Reusable for any scroll hint — check.diagnose's card-picker
   * pill and the generic floating overflow indicator (wireOverflowHint)
   * both call this with their own dismissed-state class name — pass the
   * step id it belongs to and it handles its own dismiss-memory and
   * listener cleanup.
   */
  function wireScrollFadeHint(hintEl, stepId, dismissedClass) {
    dismissedClass = dismissedClass || 'gg-wz-choice-cards-hint--dismissed';
    var key = 'gg-wz-hint-seen:' + stepId;
    try {
      if (sessionStorage.getItem(key) === '1') {
        hintEl.classList.add(dismissedClass);
        return;
      }
    } catch (e) { /* storage unavailable (private mode etc) — just skip the memory, hint still works this load */ }

    var main = document.querySelector('.gg-wz-main');
    if (!main) return;

    function onScroll() {
      if (!hintEl.isConnected) { main.removeEventListener('scroll', onScroll); return; }
      if (main.scrollTop <= 24) return;
      hintEl.classList.add(dismissedClass);
      try { sessionStorage.setItem(key, '1'); } catch (e) { /* fine, just won't be remembered */ }
      main.removeEventListener('scroll', onScroll);
    }
    main.addEventListener('scroll', onScroll, { passive: true });
  }

  var Wizard = {
    root: null,
    main: null,
    actions: null,
    chromeEl: null,
    phasesEl: null,
    moldHeaderEl: null,
    currentStepDef: null,
    locked: false,

    init: function (root) {
      this.root = root;
      this.main = root.querySelector('#gg-wz-main');
      this.actions = root.querySelector('#gg-wz-actions');
      this.chromeEl = root.querySelector('#gg-wz-chrome');
      this.phasesEl = root.querySelector('#gg-wz-phases');
      this.moldHeaderEl = root.querySelector('#gg-wz-mold-header');
    },

    /**
     * Called after prefs change from OUTSIDE the currently-rendered step —
     * specifically the header mute icon. Without this, toggling sound off in
     * the header while prep.experience is on screen leaves its switch rows
     * showing the old (now wrong) state until the customer navigates away
     * and back — the two controls read as disconnected. Re-renders in place
     * without pushing history or re-firing step analytics, since nothing
     * about *navigation* happened.
     */
    refreshIfPrefsStep: function () {
      if (this.currentStepDef && this.currentStepDef.type === 'toggles' && this.currentStepDef.store === 'prefs') {
        this.refreshCurrentStep();
      }
    },

    /**
     * Same silent in-place re-render as refreshIfPrefsStep, generalised —
     * used after a state change made from *outside* the normal render path
     * that a currently-visible action's `visibleIf` depends on. Concretely:
     * a successful replacement submission sets `replacementRequested`,
     * which `triage.destroyed`/`.bitthrough`/`.spent`'s "Okay, got it"
     * gates on — without this, that button would only pick up the change
     * the next time the customer happened to navigate away and back.
     */
    refreshCurrentStep: function () {
      if (this.currentStepDef) this.render(this.currentStepDef.__id, { silent: true });
    },

    /**
     * Resolves a `next` spec through the router and renders the result.
     * Bails out entirely while a transition is in flight — the primary
     * defence against tap-through (CSS `pointer-events` is the second
     * layer; see the .gg-wz-locked comment in wizard.css). A click event
     * that was already queued in the browser's event loop before the CSS
     * class took effect would otherwise still reach here.
     */
    advance: function (next, opts) {
      if (this.locked) return;
      var target = Router.resolve(next);
      if (!target) return;
      if (!(opts && opts.isBack)) State.pushHistory(State.data.currentStep);
      this.render(target);
    },

    /**
     * Two-phase transition: if a step is already on screen, play its exit
     * animation, THEN swap the DOM and play the new step's enter animation.
     * The wizard is locked (JS flag + CSS pointer-events) for the entire
     * window, released only once the enter animation has actually had time
     * to finish.
     *
     * Silent refreshes (prefs changed via the header icon while a toggles
     * step is showing) skip all of this — nothing conceptually navigated, so
     * animating and locking would just be visual noise for no reason.
     */
    render: function (stepId, opts) {
      var silent = Boolean(opts && opts.silent);
      var self = this;

      var step = flow.steps[stepId];
      if (!step) {
        console.warn('[wizard] unknown step id, falling back to start:', stepId);
        stepId = flow.settings.startStep;
        step = flow.steps[stepId];
      }
      step.__id = stepId;

      // Leaving a running timer step for anywhere else (including Back)
      // stops it — releases the wake lock and clears the persisted
      // countdown, since it's no longer relevant to wherever we're going.
      if (this.currentStepDef &&
          this.currentStepDef.type === 'timer' &&
          this.currentStepDef.__id !== stepId) {
        Timer.stop();
      }

      var existing = silent ? null : this.main.querySelector('.gg-wz-step');
      var budget = transitionBudget();

      var swap = function () {
        self.currentStepDef = step;
        State.goto(stepId);

        var stepEl = el('div', 'gg-wz-step');
        stepEl.setAttribute('data-step', stepId);
        stepEl.appendChild(Renderer.build(step));
        if (!silent) {
          stepEl.classList.add('gg-wz-step-enter');
          if (budget.enter) stepEl.style.animationDuration = budget.enter + 'ms';
        }

        self.main.innerHTML = '';
        self.main.appendChild(stepEl);
        self.main.scrollTop = 0;

        self.actions.innerHTML = '';
        self.actions.appendChild(Renderer.buildActions(step));
        // A step can legitimately have no bottom-bar action at all now
        // (its primary "Continue" moved inline into the content instead,
        // e.g. read-heavy Care screens) — collapse the bar the same way
        // setPrimaryHidden does for a mid-timer empty bar, not just during
        // timers.
        self.actions.classList.toggle('gg-wz-actions--empty', self.actions.children.length === 0);

        // Wake lock spans the whole touchless sequence (boil → cool → mold),
        // not each individual timer — flickering it on/off between every
        // step meant repeated re-acquisition, which is exactly where iOS's
        // Wake Lock implementation is flakiest. `holdWake` is a flow-JSON
        // flag on every step in that sequence; the first step without it
        // (cp.molded) is where it naturally releases.
        if (!silent) {
          if (step.holdWake) wakeLockCtl.request(); else wakeLockCtl.release();
        }

        // Ahead of wireTimer: the mold header's segments must exist and be
        // in the right done/current/todo state before wireTimer looks up
        // "the current segment" to drive its live fill.
        self.renderMoldChrome(step);

        if (step.gate === 'allChecked') Wizard.wireGate(stepEl, self.actions);
        if (step.type === 'timer') Wizard.wireTimer(stepId, step, stepEl, self.actions);
        if (!silent && step.type === 'content' && step.chain && step.chain.autoAdvance) {
          Wizard.wireContentChain(stepId, step, stepEl, self.actions);
        }
        if (step.reviewWidget) {
          var mountEl = stepEl.querySelector('.gg-wz-review-mount');
          if (mountEl) ReviewAdapter.mount(mountEl);
        }

        // First arrival at cp.success in this saved session only — confetti
        // every time it's revisited (e.g. via Back) would just be noise.
        // `celebrated` lives in state.data alongside attempts/diagnosis, so
        // it resets exactly when the rest of a fresh attempt does. Mounted
        // on self.root (#gg-wizard), not stepEl: stepEl carries the
        // step-enter transform, and a `position: fixed` descendant of a
        // transformed element is contained by it instead of the viewport
        // — confirmed dead-on-arrival that way. #gg-wizard also owns the
        // --gg-wz-* custom properties the pieces are coloured with, which a
        // sibling under document.body wouldn't inherit.
        if (!silent && step.celebrate && !State.get('celebrated', false)) {
          State.set({ celebrated: true });
          Confetti.burst(self.root);
        }

        if (!silent) {
          // Programmatic only — for screen readers, not stolen from a real
          // tab order. See the CSS note by .gg-wz-title:focus.
          var heading = stepEl.querySelector('h1');
          if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }

          track('gg_wizard_step', {
            wizard_step: stepId,
            wizard_phase: step.phase,
            wizard_elapsed_ms: Date.now() - State.data.startedAt
          });

          var release = function () { self.locked = false; self.root.classList.remove('gg-wz-locked'); };
          if (budget.enter) setTimeout(release, budget.enter); else release();

          requestAnimationFrame(function () { self.wireOverflowHint(stepId, step); });
        }

        debug.refresh();
      };

      if (silent || !existing || !budget.exit) {
        swap();
        return;
      }

      this.locked = true;
      this.root.classList.add('gg-wz-locked');
      existing.classList.add('gg-wz-step-exit');
      existing.style.animationDuration = budget.exit + 'ms';
      setTimeout(swap, budget.exit);
    },

    renderPhases: function (phase) {
      var order = (flow.settings.phases || []).map(function (p) { return p.id; });
      var index = order.indexOf(phase);
      Array.prototype.forEach.call(this.phasesEl.children, function (li, i) {
        li.setAttribute('data-state', i < index ? 'done' : i === index ? 'current' : 'todo');
      });
    },

    /**
     * The mold header — Cooldown/Boil/Impression/Cool — takes over the
     * exact spot `.gg-wz-phases` normally occupies, for as long as the
     * current step declares a `moldStage`. Not a second bar stacked
     * alongside the first; `.gg-wz-chrome--mold` inverts the whole bar
     * (blue ground, white labels) so the takeover itself reads as "a
     * different, timed process," per the agreed design. Reverts to the
     * normal phase bar the instant a step without `moldStage` renders —
     * `cp.molded`, the very next step after `mold.cool`, has none, so the
     * release happens for free on the next render.
     *
     * Each segment mirrors `.gg-wz-phase`'s own done/current/todo grammar
     * (see renderPhases above and the CSS) — same states, same meaning,
     * just inverted colors and, on the current segment, a live fill driven
     * by `--gg-wz-fill` instead of a fixed 50/50 split. wireTimer's onTick
     * is what actually updates that variable every tick; this method only
     * resets it to 0% on a fresh mount so a stage never opens showing the
     * previous stage's leftover fill.
     */
    renderMoldChrome: function (step) {
      var stageId = step.moldStage;
      if (!stageId) {
        this.chromeEl.classList.remove('gg-wz-chrome--mold');
        this.phasesEl.hidden = false;
        this.moldHeaderEl.hidden = true;
        this.renderPhases(step.phase);
        return;
      }

      this.chromeEl.classList.add('gg-wz-chrome--mold');
      this.phasesEl.hidden = true;
      this.moldHeaderEl.hidden = false;

      var stages = flow.settings.moldStages || [];
      var order = stages.map(function (s) { return s.id; });
      var index = order.indexOf(stageId);

      // The <li> count is built once, then just reused — same
      // lazy-build-then-reuse pattern as everywhere else a static shell
      // holds dynamic children. Label text and data-stage, though, are
      // resynced from flow.json on every render, not just at build time —
      // an earlier version only set them inside this if-block, which
      // looked like it made the JSON authoritative but didn't: the
      // server-rendered fallback already ships 4 <li>s, so the count
      // check here is false on the very first real render too, and a
      // label changed in JSON would silently never reach the page at all.
      if (this.moldHeaderEl.children.length !== stages.length) {
        this.moldHeaderEl.innerHTML = '';
        stages.forEach(function () {
          this.moldHeaderEl.appendChild(el('li', 'gg-wz-mold-seg'));
        }, this);
      }

      Array.prototype.forEach.call(this.moldHeaderEl.children, function (li, i) {
        var s = stages[i];
        if (s) {
          li.textContent = s.label;
          li.setAttribute('data-stage', s.id);
        }
        li.style.setProperty('--gg-wz-fill', '0%');
        li.setAttribute('data-state', i < index ? 'done' : i === index ? 'current' : 'todo');
      });
    },

    /**
     * `step.gate === 'allChecked'` disables the primary action until every
     * non-optional toggle row is checked — makes it visually obvious there's
     * something still required, rather than letting someone continue past a
     * checklist that quietly wasn't complete. Optional rows (mirror,
     * scissors) are excluded via `data-optional`, set by buildToggles.
     *
     * Real `disabled` on the button, not just a style — browsers won't
     * dispatch click on a disabled button at all, so this needs no
     * coordination with advance()'s lock check.
     *
     * Wired here rather than inside buildToggles because the primary button
     * lives in a sibling container (#gg-wz-actions), built separately from
     * the step's own content — this is the one place both are already in
     * the live DOM together.
     */
    wireGate: function (stepEl, actionsEl) {
      var primaryBtn = actionsEl.querySelector('.gg-wz-btn--primary');
      if (!primaryBtn) return;

      function evaluate() {
        var required = stepEl.querySelectorAll('.gg-wz-toggle-row[data-optional="false"]');
        var complete = required.length === 0 ||
          Array.prototype.every.call(required, function (row) { return row.getAttribute('aria-pressed') === 'true'; });
        primaryBtn.disabled = !complete;
      }

      evaluate();
      // Event delegation: the toggle rows' own click handlers (attached in
      // buildToggles) run at the target phase and update aria-pressed BEFORE
      // this bubble-phase listener fires, so `evaluate()` always sees the
      // post-click state without needing a setTimeout.
      stepEl.addEventListener('click', function (e) {
        if (e.target.closest('.gg-wz-toggle-row')) evaluate();
      });
    },

    /**
     * The auto-advance chain, per ENGINE_SPEC §3/§6.3 and CUSTOMER_JOURNEY
     * §5's "wet-hands chain". Rebuilt after real-device feedback: the
     * banner + progress bar now show for the FULL countdown from the
     * moment the step is ready to advance — not silently for most of it
     * with only a bare "Continue" button, then a 3-second banner right at
     * the end. The manual button is hidden entirely while auto-advance is
     * live (Hold reveals it again) rather than sitting there redundantly
     * next to a countdown that's already doing its job — having both
     * visible at once read as "which one do I use?"
     *
     * `totalSeconds` is the FULL visible window — for a content step this
     * is its own "read this, then move on" time; for a step chained off a
     * timer's completion it's the post-completion lead-in. Either way the
     * countdown is honest: what's shown is exactly how long until it fires.
     *
     * Structural rule enforced by its call sites (wireTimer's onComplete
     * and fireReady, and wireContentChain) rather than here: this is only
     * ever invoked with the `next` of a step's
     * single primary action, never with a conditional/decision route — so
     * it is mechanically incapable of auto-advancing past a decision.
     */
    /**
     * A small floating "there's more below" arrow for any step whose
     * content genuinely overflows the viewport. Runs alongside
     * check.diagnose's own tailored choiceHint pill (per Jay's rev-7 ask,
     * for visual consistency across every long page) rather than backing
     * off for it — the two are visually unified now (see
     * .gg-wz-choice-cards-hint's styling) so showing both isn't a
     * duplicate cue, it's the same cue twice at different scroll depths.
     * Detected generically after layout settles rather than flagged
     * per-step in the flow JSON, so it can't go stale as copy changes push
     * a step over the fold that didn't used to be. Fades and remembers
     * itself dismissed for the step this session, via the same
     * wireScrollFadeHint used by the card-picker hint.
     *
     * Skipped entirely on moldStage steps — per Jay, the stepper is always
     * one screen's worth of content with a layout that shifts as coaching
     * items expand/collapse, so a persistent "more below" cue is more
     * misleading there than helpful.
     *
     * Also skipped when the step's own primary action is already fully
     * visible — on a page that's only marginally taller than the
     * viewport, something less essential below it (a Back link, say)
     * being clipped isn't worth surfacing a hint for; the thing the hint
     * exists to point at is already on screen.
     *
     * Appended as a sibling of stepEl directly inside .gg-wz-main, not
     * inside stepEl itself and not position:fixed — `position: sticky`
     * against .gg-wz-main (the actual scrolling element) pins it to the
     * bottom of the *content* area for free, without ever overlapping the
     * actions bar below (a fixed-position badge would've floated on top
     * of whatever's visually at that screen position, actions bar
     * included). Cleared automatically on the next render, since
     * self.main.innerHTML='' already wipes every child of .gg-wz-main,
     * this one included.
     */
    wireOverflowHint: function (stepId, step) {
      if (step.moldStage) return;
      var main = this.main;
      if (main.scrollHeight <= main.clientHeight + 4) return; // +4 for subpixel rounding

      var primaryEl = main.querySelector('.gg-wz-btn--primary');
      if (primaryEl) {
        var primaryRect = primaryEl.getBoundingClientRect();
        var mainRect = main.getBoundingClientRect();
        if (primaryRect.bottom <= mainRect.bottom + 4) return;
      }

      var key = 'gg-wz-hint-seen:' + stepId;
      try {
        if (sessionStorage.getItem(key) === '1') return;
      } catch (e) { /* private mode etc — just show it, no memory this load */ }

      var hint = el(
        'button',
        'gg-wz-scroll-hint',
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>'
      );
      hint.type = 'button';
      hint.setAttribute('aria-label', 'Scroll down for more');
      hint.addEventListener('click', function () {
        // Clamped to whatever's actually left to scroll, computed fresh at
        // click time — a flat 70%-of-viewport jump could ask to scroll
        // past the real bottom, and while the browser clamps that for a
        // single scroll, a page whose height was still settling (a lazy
        // image finishing decode) could make that clamp land somewhere
        // that reads as an overshoot-then-snap-back once layout settled.
        // Computing the true max at the moment of the click removes the
        // ambiguity either way.
        var maxScroll = Math.max(0, main.scrollHeight - main.clientHeight);
        var target = Math.min(maxScroll, main.scrollTop + Math.round(main.clientHeight * 0.7));
        main.scrollTo({ top: target, behavior: reducedMotion() ? 'auto' : 'smooth' });
      });
      main.appendChild(hint);
      wireScrollFadeHint(hint, stepId, 'gg-wz-scroll-hint--dismissed');
    },

    beginChainAdvance: function (stepEl, actionsEl, primaryNext, label, totalSeconds) {
      if (!(config.features && config.features.autoAdvance)) return;

      var total = totalSeconds || (config.features && config.features.autoAdvanceLeadIn) || 4;
      var done = false;
      var primaryBtn = actionsEl.querySelector('.gg-wz-btn--primary');
      setPrimaryHidden(primaryBtn, true);

      var banner = el(
        'div',
        'gg-wz-chain-banner',
        '<div class="gg-wz-chain-track"><div class="gg-wz-chain-fill"></div></div>' +
        '<div class="gg-wz-chain-row">' +
        '<span class="gg-wz-chain-label">Next: ' + label + '. <span class="gg-wz-chain-count">' + total + '</span>…</span>' +
        '<button type="button" class="gg-wz-btn gg-wz-btn--text gg-wz-chain-extend">+5s</button>' +
        '</div>'
      );
      stepEl.appendChild(banner);

      var fillEl = banner.querySelector('.gg-wz-chain-fill');
      fillEl.style.transitionDuration = (total * 1000) + 'ms';
      // Two rAFs so the browser commits the 0% starting state as a real
      // paint before the transition to 100% is triggered — otherwise it can
      // collapse into the end state instantly with no visible animation.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { fillEl.style.width = '100%'; });
      });

      // Timestamp-based, like the main timer above — a plain "decrement by 1
      // every setInterval(…, 1000)" drifts, since neither the browser's
      // interval spacing nor the main thread's availability is exactly
      // 1000ms tick to tick, so the 3-2-1 chime ended up audibly uneven.
      // Polling every 250ms and deriving the displayed second from
      // `endsAt - Date.now()` keeps the beeps locked to real wall-clock time
      // regardless of interval jitter.
      var endsAt = Date.now() + total * 1000;
      var countEl = banner.querySelector('.gg-wz-chain-count');
      var lastShown = total;

      function cleanup() {
        done = true;
        clearInterval(interval);
        if (banner.parentNode) banner.remove();
      }

      var interval = setInterval(function () {
        var remaining = Math.round((endsAt - Date.now()) / 1000);
        if (remaining <= 0) { cleanup(); Wizard.advance(primaryNext); return; }
        if (remaining === lastShown) return; // no whole second has ticked over yet
        lastShown = remaining;
        countEl.textContent = remaining;
        if (remaining <= 3) cues.tick(remaining); // 3-2-1 lead-in, per the request for an audible cue
      }, 250);

      // A delay, not a cancel — this used to fully stop the auto-advance
      // and hand control back to a manual button, which meant a single tap
      // here could strand someone out of the touchless flow entirely. +5s
      // just pushes this one countdown out a bit and keeps going; nothing
      // needs re-arming afterward.
      banner.querySelector('.gg-wz-chain-extend').addEventListener('click', function () {
        var remaining = Math.max(0, Math.round((endsAt - Date.now()) / 1000));
        var elapsedSec = total - remaining;
        total += 5;
        endsAt += 5000;
        remaining += 5;
        lastShown = remaining;
        countEl.textContent = remaining;

        var currentPct = Math.max(0, Math.min(100, (elapsedSec / total) * 100));
        fillEl.style.transitionDuration = '0ms';
        fillEl.style.width = currentPct + '%';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            fillEl.style.transitionDuration = (remaining * 1000) + 'ms';
            fillEl.style.width = '100%';
          });
        });

        showPlusFiveBadge(countEl);
        track('gg_wizard_chain_extend', { wizard_step: stepEl.getAttribute('data-step') });
      });
    },

    /**
     * The focal get-ready countdown — opt-in via step.chain.autoAdvance.focal,
     * used on two transitions: mold.pour_cool → mold.heat ("Get ready" /
     * "LET'S GO") and mold.heat → mold.shape ("Get ready" / "Lift it out!",
     * via the optional step.chain.autoAdvance.flashLabel — defaults to
     * "LET'S GO" when omitted). Deliberately NOT a variant of
     * beginChainAdvance — that function and its remaining call sites in
     * wireTimer/wireContentChain are untouched, so every non-focal chain
     * transition in the app behaves exactly as it did before this existed.
     * Reverting either usage just means removing `focal: true` from that
     * step's wizard-flow.json entry; no code path here needs to change.
     *
     * Driven directly by the step's own real countdown rather than a
     * separate timer started once "Ready" fires — see the focal branches
     * inside wireTimer's onTick below (one for mold.pour_cool's case, where
     * the "ready" threshold sits inside a longer overshoot window; one for
     * mold.heat's, which has no overshoot and whose own Timer completion IS
     * the "GO" moment), which call enter/update/finish as the real elapsed
     * time crosses into the last few seconds and then past zero. That's what
     * makes this read as one continuous countdown instead of "the timer
     * finishes, THEN a new 5-2-1 starts" — there's only ever one clock. No
     * "+5s" extend control, unlike the banner — this is a fixed few-second
     * beat, not a wait someone might get pulled away from.
     */
    enterFocalGetReady: function (stepEl, actionsEl, directive) {
      var primaryBtn = actionsEl.querySelector('.gg-wz-btn--primary');
      setPrimaryHidden(primaryBtn, true);
      stepEl.classList.add('gg-wz-step--getready');

      var view = el(
        'div',
        'gg-wz-getready',
        '<p class="gg-wz-getready-eyebrow">Get ready</p>' +
        '<p class="gg-wz-getready-directive">' + interpolate(directive || '') + '</p>' +
        '<div class="gg-wz-getready-num"></div>' +
        '<div class="gg-wz-getready-track"><div class="gg-wz-getready-bar"></div></div>'
      );
      stepEl.appendChild(view);
      return view;
    },

    updateFocalGetReady: function (stepEl, remainingWhole, pct) {
      var view = stepEl.querySelector('.gg-wz-getready');
      if (!view) return;
      view.querySelector('.gg-wz-getready-num').textContent = remainingWhole;
      view.querySelector('.gg-wz-getready-bar').style.width = pct + '%';
    },

    finishFocalGetReady: function (stepEl, primaryNext, flashLabel) {
      var overlay = el('div', 'gg-wz-getready-flash', flashLabel || "LET'S GO");
      // Appended to the wizard root, not stepEl — stepEl is about to be
      // torn down by Wizard.advance() and this needs to survive that.
      (document.getElementById(ROOT_ID) || document.body).appendChild(overlay);
      cues.chime(true); // ascending — reads as "starting," distinct from the plain tick
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { overlay.classList.add('gg-wz-getready-flash--in'); });
      });
      setTimeout(function () {
        overlay.classList.remove('gg-wz-getready-flash--in');
        Wizard.advance(primaryNext);
        setTimeout(function () { if (overlay.parentNode) overlay.remove(); }, 300);
      }, 260 + 220);
    },

    /**
     * Content-type chain steps — the countdown itself IS the read-then-
     * move-on window now (see beginChainAdvance's header comment), so this
     * just resolves the duration and hands off immediately; there's no
     * separate silent "dwell" phase before something becomes visible.
     * Guarded structurally: only fires for a single-primary-action content
     * step, never a choice/toggles/decision step, so a JSON authoring
     * mistake degrades to "chain silently doesn't happen" rather than
     * skipping a decision. Not currently used inside the MOLD run itself —
     * mold.remove/place/bite were folded into mold.heat/mold.shape as
     * checklist stages on a single timer instead, since a full screen swap
     * mid-physical-action was more disruptive than useful — but stays
     * available for any future content-only auto-advance step.
     */
    wireContentChain: function (stepId, step, stepEl, actionsEl) {
      var actions = step.actions || [];
      var eligible = actions.length === 1 && typeof actions[0].next === 'string';
      if (!eligible) {
        console.warn('[wizard] chain.autoAdvance ignored — not a single-action content step:', stepId);
        return;
      }

      var seconds = (step.chain.autoAdvance.after != null)
        ? step.chain.autoAdvance.after
        : (config.features && config.features.autoAdvanceLeadIn) || 4;

      Wizard.beginChainAdvance(stepEl, actionsEl, actions[0].next, step.chain.autoAdvance.label, seconds);
    },

    /**
     * Timer-type steps: resolves duration, resumes a persisted countdown if
     * one's valid for this exact step, otherwise starts fresh. The primary
     * action starts disabled — real `disabled`, so no click can reach it —
     * and only enables on completion, alongside the end chime Timer itself
     * plays. Every timer gets a Restart control regardless of whether the
     * step defines one, per ENGINE_SPEC §6.1 ("every timer has a visible
     * restart").
     */
    wireTimer: function (stepId, step, stepEl, actionsEl) {
      var valueEl = stepEl.querySelector('.gg-wz-timer-value');
      var barEl = stepEl.querySelector('.gg-wz-timer-bar');
      var coachItems = stepEl.querySelectorAll('.gg-wz-coach-item');
      var extendBtn = stepEl.querySelector('.gg-wz-timer-extend');
      var notBoiledBtn = stepEl.querySelector('.gg-wz-timer-notboiled');
      var primaryBtn = actionsEl.querySelector('.gg-wz-btn--primary');

      // Set by renderMoldChrome, which always runs before wireTimer (see
      // swap()) — the segment matching this step's own moldStage, already
      // marked data-state="current". null on every non-mold timer step.
      var moldSegEl = step.moldStage ? Wizard.moldHeaderEl.querySelector('[data-state="current"]') : null;

      var seconds = resolveTimerSeconds(step.timer || {});

      // Decided once, from the stage's own starting duration, not
      // re-checked every tick — otherwise a stage that starts over a
      // minute (mold.pour_cool's cooldown) would visibly flip formats
      // mid-countdown ("1:00" → "59") the instant it crossed the 60s mark,
      // which reads as a glitch, not a format choice. A +5s nudge near
      // that boundary doesn't reclassify the stage either, for the same
      // reason — it's still fundamentally the same kind of countdown.
      var useClockFormat = seconds > 59;

      // `atFraction` (0–1) resolves relative to THIS step's own resolved
      // duration — used by mold.pour_cool so its stages stay proportionally
      // placed if a merchant reconfigures `timer_water_cool` in the theme
      // editor, rather than landing at a fixed second count that no longer
      // matches a changed total. `beforeEnd` is the opposite case — a fixed
      // lead time counted back from the ready threshold regardless of total
      // duration (the last coaching beat, "have your spoon and guard within
      // reach," needs to land in the last ~15s whatever the configured
      // total is). Every other stage keeps using plain `at` seconds,
      // unchanged.
      var coaching = ((step.timer && step.timer.coaching) || []).filter(function (entry) {
        return !entry.visibleIf || evalCondition(entry.visibleIf);
      }).map(function (entry) {
        if (entry.atFraction == null && entry.beforeEnd == null) return entry;
        var copy = {};
        for (var k in entry) { if (Object.prototype.hasOwnProperty.call(entry, k)) copy[k] = entry[k]; }
        if (entry.atFraction != null) {
          copy.at = Math.round(entry.atFraction * seconds);
        } else {
          // `beforeEnd` is a fixed real-world lead time, not derived from
          // `seconds` the way `atFraction` is — resolveTimerSeconds already
          // divides `seconds` itself by 8 under DEBUG_FAST, but this value
          // needs the same treatment explicitly or it stops meaning
          // anything once the total it's measured against shrinks that much.
          var beforeEnd = DEBUG_FAST ? Math.max(1, Math.round(entry.beforeEnd / 8)) : entry.beforeEnd;
          copy.at = Math.max(0, Math.round(seconds - beforeEnd));
        }
        return copy;
      });
      var allStages = coaching;

      // mold.pour_cool only: the underlying Timer actually runs for the
      // full overshoot window (waterOvershoot, 360s default) — `seconds`
      // above is just the "ready" threshold reached partway through it.
      // Past that threshold the digits read "Ready" and chain-advance
      // begins, same as a normal timer's onComplete; the real onComplete
      // below only fires if the whole overshoot window elapses with nobody
      // around, and routes back to re-boil. See CANONICAL_FACTS.md and
      // ENGINE_SPEC §6.3. Every other timer step leaves this null and
      // behaves exactly as it always has.
      var overshootKey = step.timer && step.timer.overshootKey;
      var overshootSeconds = overshootKey ? resolveTimerSeconds({ durationKey: overshootKey }) : null;
      var readyFired = false;

      // v2 opt-in — see enterFocalGetReady's header comment. focalLeadIn is
      // read in real seconds (not DEBUG_FAST-scaled again here) since
      // `seconds`/`elapsedSec` below are already on the scaled clock.
      var focalAutoAdvance = Boolean(step.chain && step.chain.autoAdvance && step.chain.autoAdvance.focal);
      var focalLeadIn = (step.chain && step.chain.autoAdvance && step.chain.autoAdvance.after != null)
        ? step.chain.autoAdvance.after
        : 5;
      var focalActive = false;
      var focalDone = false;
      var focalLastShown = null;

      // v3 — retires the old "count to 0, THEN start a separate 3-2-1
      // banner" pattern (beginChainAdvance) for non-focal timer steps.
      // mold.shape/mold.cool chain into the next stage this way: the real
      // countdown is the only clock, this cue just narrates its final few
      // seconds in place, and the transition fires the instant that real
      // clock hits 0 — never a moment later. Focal steps (mold.pour_cool,
      // mold.heat — see enterFocalGetReady) don't use this at all; their
      // own final seconds are the full-view "Get ready" takeover instead.
      // No "+5s" extend on this cue, unlike the old banner — the window's
      // short enough now (3s by default) that a snooze control would just
      // reintroduce the uncertainty this was built to remove.
      var nextUpEl = stepEl.querySelector('.gg-wz-timer-nextup');
      var autoAdvanceEnabled = Boolean(config.features && config.features.autoAdvance);
      var chainAutoAdvance = Boolean(step.chain && step.chain.autoAdvance && !step.chain.autoAdvance.focal);
      var chainLabel = chainAutoAdvance ? step.chain.autoAdvance.label : '';
      var chainLeadIn = (chainAutoAdvance && autoAdvanceEnabled)
        ? ((step.chain.autoAdvance.after != null) ? step.chain.autoAdvance.after : (config.features && config.features.autoAdvanceLeadIn) || 3)
        : 0;
      if (chainLeadIn >= seconds) chainLeadIn = Math.max(1, seconds - 1);
      var chainLastShown = null;

      setPrimaryHidden(primaryBtn, true);

      var mountedIllustration = null;

      // active | done | gone | todo per item, relative to the given index.
      // "Ever-moving" list — real-device feedback on the 5-stage mold.shape
      // checklist was that it accumulated every completed stage and got
      // cluttered by "Hold". Only the immediately preceding stage stays
      // visible as a checkmark next to the active one; anything completed
      // before that collapses out of the list entirely rather than sitting
      // there dimmed.
      //
      // The active item's illustration is mounted directly inside it here,
      // and unmounted the moment it stops being active — never built for
      // a todo item, never left behind on a done/gone one. Same
      // battery/perf reasoning as before (no point decoding an image or
      // running a Lottie nobody's looking at), just moved from a single
      // swapped element above the list into whichever item is actually
      // live, per the agreed worked example.
      function setCoachIndex(index) {
        for (var i = 0; i < coachItems.length; i++) {
          var state = i === index ? 'active' : i === index - 1 ? 'done' : i < index - 1 ? 'gone' : 'todo';
          coachItems[i].setAttribute('data-state', state);
        }

        if (mountedIllustration && mountedIllustration.parentNode) {
          mountedIllustration.parentNode.removeChild(mountedIllustration);
        }
        mountedIllustration = null;

        var stage = allStages[Math.min(index, allStages.length - 1)];
        if (stage && stage.media && index < coachItems.length) {
          mountedIllustration = Media.build(stage.media);
          mountedIllustration.classList.add('gg-wz-coach-illus');
          coachItems[index].querySelector('.gg-wz-coach-text').appendChild(mountedIllustration);
        }

        // A preliminary warning callout has done its job once the
        // customer's moved past the first coaching beat — leaving it up
        // for the rest of a multi-minute wait just adds clutter to a
        // screen that's meant to stay calm.
        stepEl.classList.toggle('gg-wz-step--panel-faded', index >= 1);
      }

      function fireReady() {
        if (readyFired) return;
        readyFired = true;
        valueEl.classList.add('gg-wz-timer-value--word');
        valueEl.textContent = 'Ready';
        barEl.style.width = '100%';
        if (moldSegEl) moldSegEl.style.setProperty('--gg-wz-fill', '100%');
        setCoachIndex(allStages.length);
        setPrimaryHidden(primaryBtn, false);

        // fireReady only ever runs for a non-focal overshoot step (the
        // focal case returns earlier in onTick's overshoot branch above,
        // before fireReady is ever called) — so this is always the plain
        // banner, never the focal takeover.
        if (step.chain && step.chain.autoAdvance && primaryBtn) {
          var readyLeadIn = step.chain.autoAdvance.after != null
            ? step.chain.autoAdvance.after
            : (config.features && config.features.autoAdvanceLeadIn) || 4;
          var readyNext = (step.actions || [])[0] && step.actions[0].next;
          Wizard.beginChainAdvance(stepEl, actionsEl, readyNext, step.chain.autoAdvance.label, readyLeadIn);
        }
      }

      var opts = {
        coaching: coaching,
        onTick: function (remainingMs, totalMs) {
          var elapsedSec = (totalMs - remainingMs) / 1000;

          if (overshootSeconds != null && elapsedSec >= seconds) {
            // v2: the focal countdown already covered this last stretch
            // (see below) — crossing the threshold is the "GO" moment
            // itself, not a separate "Ready" state to fire first.
            if (focalAutoAdvance) {
              if (!focalDone) {
                focalDone = true;
                Wizard.finishFocalGetReady(stepEl, (step.actions || [])[0] && step.actions[0].next, step.chain.autoAdvance.flashLabel);
              }
              return;
            }
            if (!readyFired) fireReady();
            return;
          }

          // v2: the last `focalLeadIn` seconds before the ready threshold
          // are the SAME clock as the countdown above, just re-rendered —
          // there's no separate timer starting once this one "finishes,"
          // so nothing before this point is skipped and nothing after it
          // double-counts. mold.pour_cool's own "ready" threshold sits
          // partway through a longer overshoot window (below); mold.heat
          // has no overshoot at all — its own Timer total IS `seconds`, so
          // this same "last focalLeadIn seconds" window is driven directly
          // off that, in the branch further down.
          if (focalAutoAdvance && overshootSeconds != null) {
            var remainingToReady = seconds - elapsedSec;
            if (remainingToReady <= focalLeadIn) {
              if (!focalActive) {
                focalActive = true;
                Wizard.enterFocalGetReady(stepEl, actionsEl, step.chain.autoAdvance.directive || step.chain.autoAdvance.label);
              }
              var remainingWhole = Math.max(1, Math.ceil(remainingToReady));
              var focalPct = Math.min(100, Math.max(0, 100 * (1 - remainingToReady / focalLeadIn)));
              Wizard.updateFocalGetReady(stepEl, remainingWhole, focalPct);
              if (remainingWhole !== focalLastShown) {
                focalLastShown = remainingWhole;
                if (remainingWhole <= 3) cues.tick(remainingWhole);
              }
              if (moldSegEl) moldSegEl.style.setProperty('--gg-wz-fill', (100 * (elapsedSec / seconds)) + '%');
              return;
            }
          }

          // v4: focal WITHOUT an overshoot window — mold.heat. There's no
          // separate "ready" threshold to cross mid-Timer here; `seconds`
          // IS the Timer's own total, so its natural completion (Timer's
          // finish(), which calls onTick(0,…) then onComplete()) is the
          // "GO" moment — handled in onComplete below, not here. This
          // branch only needs to cover the countdown-side view: the last
          // `focalLeadIn` seconds becoming the full-view "Get ready"
          // takeover before that natural completion happens.
          if (focalAutoAdvance && overshootSeconds == null) {
            var remainingToFocalEnd = seconds - elapsedSec;
            if (remainingToFocalEnd <= focalLeadIn) {
              if (!focalActive) {
                focalActive = true;
                Wizard.enterFocalGetReady(stepEl, actionsEl, step.chain.autoAdvance.directive || step.chain.autoAdvance.label);
              }
              var remainingWholeH = Math.max(1, Math.ceil(remainingToFocalEnd));
              var focalPctH = Math.min(100, Math.max(0, 100 * (1 - remainingToFocalEnd / focalLeadIn)));
              Wizard.updateFocalGetReady(stepEl, remainingWholeH, focalPctH);
              if (remainingWholeH !== focalLastShown) {
                focalLastShown = remainingWholeH;
                if (remainingWholeH <= 3) cues.tick(remainingWholeH);
              }
              if (moldSegEl) moldSegEl.style.setProperty('--gg-wz-fill', (100 * (elapsedSec / seconds)) + '%');
              return;
            }
          }

          // v3: the "Next up" cue for a non-focal chain.autoAdvance
          // (mold.shape, mold.cool) — appears alongside the still-ticking
          // digits for the final `chainLeadIn` seconds, never replacing
          // them, and the step advances the instant this countdown hits 0.
          if (chainLeadIn > 0 && nextUpEl) {
            var remainingToChain = seconds - elapsedSec;
            if (remainingToChain <= chainLeadIn && remainingToChain > 0) {
              var chainWhole = Math.max(1, Math.ceil(remainingToChain));
              if (chainWhole !== chainLastShown) {
                chainLastShown = chainWhole;
                nextUpEl.textContent = 'Next up: ' + chainLabel + ' in ' + chainWhole + '…';
                nextUpEl.classList.add('gg-wz-timer-nextup--active');
                cues.tick(chainWhole);
              }
            } else if (chainLastShown !== null && remainingToChain > chainLeadIn) {
              // e.g. the +5s extend pushed the deadline back out
              nextUpEl.classList.remove('gg-wz-timer-nextup--active');
              chainLastShown = null;
            }
          }

          valueEl.classList.toggle('gg-wz-timer-value--clock', useClockFormat);
          valueEl.textContent = formatCountdown(seconds - elapsedSec, useClockFormat);
          var pct = Math.min(100, Math.max(0, 100 * (elapsedSec / seconds)));
          barEl.style.width = pct + '%';
          if (moldSegEl) moldSegEl.style.setProperty('--gg-wz-fill', pct + '%');
        },
        onCoach: function (entry, index) { setCoachIndex(index); },
        onComplete: function () {
          if (overshootSeconds != null) {
            // The full overshoot window elapsed with nobody around to act
            // on "ready" — the water's gone cold enough that continuing
            // would ruin the impression. Route back to re-boil rather than
            // let the flow proceed on it.
            track('gg_wizard_timer_skipped', { wizard_step: stepId, reason: 'overshoot' });
            Wizard.advance(step.timer.overshootGoto);
            return;
          }

          track('gg_wizard_timer_complete', { wizard_step: stepId, seconds: seconds });

          // v4: mold.heat — no overshoot, so Timer's own natural
          // completion (the true end of `seconds`) IS the "GO" moment.
          // The last `focalLeadIn` seconds already ran as the full-view
          // takeover (see onTick above); this just fires the flash +
          // advance, same as pour_cool's overshoot-threshold branch does.
          if (focalAutoAdvance) {
            Wizard.finishFocalGetReady(stepEl, (step.actions || [])[0] && step.actions[0].next, step.chain.autoAdvance.flashLabel);
            return;
          }

          valueEl.textContent = '0';
          barEl.style.width = '100%';
          if (moldSegEl) moldSegEl.style.setProperty('--gg-wz-fill', '100%');

          // v3: the "Next up" cue already ran during this countdown's own
          // final seconds (see onTick above) — completion just advances
          // immediately, there's no separate lead-in left to run through.
          setCoachIndex(allStages.length); // marks every item done — nothing left "active"

          var chainNextId = (step.actions || [])[0] && step.actions[0].next;
          if (chainAutoAdvance && autoAdvanceEnabled) {
            Wizard.advance(chainNextId);
            return;
          }
          setPrimaryHidden(primaryBtn, false);
        }
      };

      if (!Timer.resume(stepId, opts)) Timer.start(stepId, overshootSeconds != null ? overshootSeconds : seconds, opts);

      // A buffer nudge, not a do-over — this used to be "Restart", which
      // threw away the whole stage's progress and coaching state. Genuinely
      // risky to leave next to a countdown someone's mid-action for, with
      // wet hands and a guard in their mouth. +5s just pushes the deadline
      // out; nothing else about the running timer changes. Works the same
      // in overshoot mode — `seconds` is the local "ready" threshold this
      // function compares elapsed time against, decoupled from Timer's own
      // internal deadline (`Timer.extend`), so nudging both by 5 keeps them
      // in sync regardless of which mode this step is in.
      if (extendBtn) {
        extendBtn.addEventListener('click', function () {
          track('gg_wizard_timer_extend', { wizard_step: stepId });
          seconds += 5;
          Timer.extend(5);
          showPlusFiveBadge(valueEl);
        });
      }

      // mold.pour_cool's own correction link — see buildTimer's doc note.
      if (notBoiledBtn) {
        notBoiledBtn.addEventListener('click', function () {
          Timer.stop();
          Wizard.advance(step.notBoiledYet);
        });
      }
    },

    /**
     * mold.pour_cool. One 360s (default) timer covers both thresholds:
     * under `durationKey` seconds elapsed → countdown + self-advancing
     * slides + a T-20s interrupt; between that and `overshootKey` → a
     * "ready, carry on whenever" state; past `overshootKey` → the water's
     * gone cold, routed back to re-boil automatically. See CANONICAL_FACTS
     * and ENGINE_SPEC §6.3.
     */
  };

  function syncHeaderMute() {
    var mute = document.querySelector('[data-gg-wz="mute"]');
    if (mute) mute.setAttribute('aria-pressed', String(prefs.sound));
  }

  // ------------------------------------------------------------------
  // Deep linking & boot sequence. See ENGINE_SPEC §5.
  // ------------------------------------------------------------------

  function parseQuery() {
    var params = new URLSearchParams(window.location.search);
    return {
      step: params.get('step'),
      fresh: params.get('fresh') === '1',
      size: params.get('size'),
      arch: params.get('arch')
    };
  }

  /**
   * Strips the wizard's own QSPs (step/fresh/size/arch/dx/fast) from the URL
   * once they've been consumed, via replaceState — no reload, no history
   * entry.
   *
   * Without this, a `?fresh=1` link stays "live" on every subsequent reload
   * of that URL — including reloads the customer triggers themselves — which
   * silently discards their progress every single time rather than just the
   * once, as intended. Found by reloading a couple of times in a row rather
   * than testing each QSP exactly once.
   *
   * `fast` was missing from this list until a real-device report of the
   * post-impression cool timer feeling far too short — the baseline is 30s
   * (matches CANONICAL_FACTS.md/QUESTIONS.md §A5), but `fast=1` divides every
   * timer by 8, and being the one debug QSP that DIDN'T get flushed, it kept
   * silently applying on every reload of a URL that had it once, long after
   * whoever added it stopped noticing it was there. Same class of bug as the
   * one above, just one QSP that got left out the first time.
   *
   * Anything else on the query string (?preview=1, utm_*, Shopify's own
   * params) is left untouched — this only removes the keys the wizard itself
   * reads. `preview` stays sticky on purpose — it's a standing mode (shows
   * the debug panel), not a one-shot action like the rest of these.
   */
  function flushConsumedQsp() {
    var url = new URL(window.location.href);
    var consumed = ['step', 'fresh', 'size', 'arch', 'dx', 'fast'];
    var changed = false;
    consumed.forEach(function (key) {
      if (url.searchParams.has(key)) { url.searchParams.delete(key); changed = true; }
    });
    if (changed) {
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  }

  function boot(root) {
    loadFlow().then(function (loaded) {
      flow = loaded;
      Wizard.init(root);

      var qs = parseQuery();
      var resumed = qs.fresh ? null : State.load();
      State.init(resumed);

      // QSP pre-seeds, applied after state exists so they persist.
      var seed = {};
      if (qs.size === 'small' || qs.size === 'large') seed.size = qs.size;
      if (qs.arch === 'upper' || qs.arch === 'lower') seed.arch = qs.arch;
      if (Object.keys(seed).length) State.set(seed);

      debug.init(Wizard, flow);

      var entryStep = null;
      var entryType = 'fresh';

      if (qs.step && flow.steps[qs.step]) {
        entryStep = qs.step;
        entryType = 'deep_link';
      } else if (resumed && resumed.currentStep && resumed.currentStep !== flow.settings.startStep) {
        resumeTarget = resumed.currentStep;
        entryStep = 'arrive.resume';
        entryType = 'resume_prompt';
      } else {
        entryStep = flow.settings.startStep;
      }

      // Consume the QSPs BEFORE rendering, so even a render-time error can't
      // leave them live for the next reload.
      flushConsumedQsp();

      track('gg_wizard_start', { flow_version: flow.flowVersion, entry: entryType });
      Wizard.render(entryStep);
    }).catch(function (err) {
      console.error('[wizard] failed to boot:', err);
      root.querySelector('#gg-wz-main').innerHTML =
        '<div class="gg-wz-step"><p class="gg-wz-eyebrow">Something went wrong</p>' +
        '<h1 class="gg-wz-title">This page couldn\'t load</h1>' +
        '<p class="gg-wz-body-text">Please refresh, or email hello@trygrindguard.com and we\'ll help directly.</p></div>';
    });
  }

  // ------------------------------------------------------------------
  // Viewport (100dvh fallback for older browsers) — unchanged from Phase 1.
  // ------------------------------------------------------------------

  function supportsDvh() {
    return window.CSS && CSS.supports && CSS.supports('height', '100dvh');
  }

  function initViewport(root) {
    if (supportsDvh()) return;
    function sync() { document.documentElement.style.setProperty('--gg-wz-vh', window.innerHeight + 'px'); }
    root.style.height = 'var(--gg-wz-vh)';
    sync();
    window.addEventListener('resize', sync, { passive: true });
    window.addEventListener('orientationchange', function () { setTimeout(sync, 120); });
  }

  /**
   * Best-effort attempt to collapse the mobile browser's address bar on
   * load, rather than waiting for the customer to scroll far enough into
   * the flow to trigger it organically. There's no sanctioned API for
   * this — browsers deliberately don't expose it — so this leans on the
   * old "nudge the window scroll position" trick. It only has anything to
   * work with because #gg-wizard fills the viewport exactly (100dvh) and
   * the actual page body otherwise has zero scrollable height, meaning
   * there was never anything for the OUTER window to scroll even by 1px —
   * every real scrolling in this app happens inside .gg-wz-main, which
   * the browser doesn't necessarily treat as equivalent to a page scroll
   * for chrome-collapse purposes. Giving the body a single extra pixel of
   * height and immediately scrolling into it is a genuine attempt, not
   * theater — but recent iOS Safari versions have deliberately hardened
   * against exactly this pattern, so don't expect it to be reliable.
   * Harmless either way: a failed attempt just leaves the page at
   * scrollTop 0, indistinguishable from never having tried.
   */
  function attemptChromeCollapse() {
    document.documentElement.style.minHeight = 'calc(100dvh + 1px)';
    window.addEventListener('load', function () {
      setTimeout(function () {
        window.scrollTo(0, 1);
      }, 50);
    });
  }

  // ------------------------------------------------------------------
  // Debug panel — ?preview=1. Stands in for the staging environment we
  // don't have. See ENGINE_SPEC §13.5.
  // ------------------------------------------------------------------

  var debug = {
    on: false,
    panel: null,
    logEl: null,
    wizard: null,
    flow: null,

    init: function (wizard, loadedFlow) {
      if (!/[?&]preview=1/.test(location.search)) return;
      this.on = true;
      this.wizard = wizard;
      this.flow = loadedFlow;

      var toggle = el('button', 'gg-wz-debug-toggle', 'debug');
      var panel = el('div', 'gg-wz-debug');
      panel.hidden = true;
      toggle.addEventListener('click', function () { panel.hidden = !panel.hidden; });

      document.body.appendChild(toggle);
      document.body.appendChild(panel);
      this.panel = panel;
      this.render();
    },

    render: function () {
      if (!this.on) return;
      var self = this;

      this.panel.innerHTML =
        '<h3>state</h3><div id="gg-wz-dbg-state"></div>' +
        '<h3>jump to step</h3><div id="gg-wz-dbg-jump"></div>' +
        '<h3>environment</h3><div id="gg-wz-dbg-env"></div>' +
        '<h3>log</h3><div id="gg-wz-dbg-log"></div>';

      var jump = this.panel.querySelector('#gg-wz-dbg-jump');
      Object.keys(this.flow.steps).forEach(function (id) {
        var b = document.createElement('button');
        b.textContent = id;
        b.addEventListener('click', function () { self.wizard.render(id); });
        jump.appendChild(b);
      });

      var reset = document.createElement('button');
      reset.textContent = '✕ clear storage';
      reset.addEventListener('click', function () {
        store.clear(STATE_KEY);
        store.clear(PREFS_KEY);
        location.reload();
      });
      jump.appendChild(reset);

      this.panel.querySelector('#gg-wz-dbg-env').textContent =
        'dvh: ' + supportsDvh() +
        ' · wakeLock: ' + ('wakeLock' in navigator) +
        ' · vibrate: ' + ('vibrate' in navigator) +
        ' · audio: ' + Boolean(cues.ctx) +
        ' · ' + window.innerWidth + '×' + window.innerHeight +
        ' · market: ' + (config.market || '?') +
        ' · flow: ' + this.flow.flowVersion;

      this.logEl = this.panel.querySelector('#gg-wz-dbg-log');
      this.refresh();
    },

    refresh: function () {
      if (!this.on || !this.panel) return;
      var el = this.panel.querySelector('#gg-wz-dbg-state');
      if (!el || !State.data) return;
      el.textContent = JSON.stringify(
        {
          step: State.data.currentStep,
          session: State.data.sessionId,
          data: State.data.data,
          history: State.data.history.slice(-6),
          prefs: prefs
        },
        null,
        1
      );
    },

    log: function (line) {
      if (!this.logEl) return;
      this.logEl.textContent = (new Date().toLocaleTimeString() + '  ' + line + '\n' + this.logEl.textContent).slice(0, 4000);
    }
  };

  // ------------------------------------------------------------------
  // Escalation overlay — help + free-replacement forms. Phase 6.
  //
  // Per CUSTOMER_JOURNEY.md §8, this is deliberately an overlay, not a flow
  // step: "Dismiss returns to their step" — closing it never calls
  // Wizard.advance(), unlike everything else in the wizard. It renders on
  // top of whatever step is currently on screen and disappears back into it.
  //
  // Posts straight to the Lambda built and live-tested in Phase 0 (see
  // services/escalation/) — same request contract as ENGINE_SPEC §7.5 and
  // services/escalation/src/validate.mjs: {type, email, orderNumber, issue,
  // message, photo:{dataUrl,name}, replacement:{...}, context:{...}}.
  // ------------------------------------------------------------------

  // Mirrors services/escalation/src/format.mjs's DIAGNOSIS_LABELS — kept in
  // sync by hand rather than shared, since one's a browser bundle and the
  // other a Lambda. If they drift, the worst case is a slightly-off prefill
  // on the issue field, not a functional break (the server treats `issue`
  // as free text, not an enum). format.mjs has no {{token}} resolution (no
  // access to config.strings/config.market), so its copy of this object
  // stays plain US-English text — keep it matching this one's US-default
  // output exactly, not the raw {{mold}}/{{molded}} tokens below.
  var DIAGNOSIS_LABELS = {
    good: 'Guard looked good',
    weak: 'Weak / faint tooth impressions',
    underheated: 'Under-heated, barely {{molded}}',
    oversoft: 'Over-heated but shape intact',
    destroyed: 'Guard twisted or collapsed',
    bitthrough: 'Bit through the guard'
  };

  /**
   * Everything the Lambda's `context` object wants (ENGINE_SPEC §7.5), read
   * fresh off live state at submit time — never cached, since the customer
   * may sit on the form for a while before sending it.
   */
  function buildEscalationContext() {
    var stepId = State.data && State.data.currentStep;
    var stepDef = stepId && flow.steps[stepId];
    return {
      step: stepId || '',
      phase: (stepDef && stepDef.phase) || '',
      diagnosis: State.get('diagnosis', ''),
      size: State.get('size', ''),
      arch: State.get('arch', ''),
      attempts: State.get('attempts', 0),
      guardsRemaining: State.get('guards', null),
      elapsedMs: State.data ? (Date.now() - (State.data.startedAt || Date.now())) : 0,
      safetyFlags: State.get('safetyFlags', []),
      sessionId: (State.data && State.data.sessionId) || '',
      flowVersion: (flow && flow.flowVersion) || '',
      market: config.market || '',
      userAgent: navigator.userAgent || ''
    };
  }

  /**
   * File → downscaled JPEG data URL. Per ENGINE_SPEC §7.4: max 1600px long
   * edge, ~0.8 quality — takes a typical multi-MB phone photo well under the
   * Lambda's 2MB cap. Never throws past the caller; a decode/read failure
   * rejects so the caller can submit without the photo rather than trap the
   * customer on a broken form (same "never fail silently" rule as the spec).
   */
  function downscalePhoto(file) {
    return new Promise(function (resolve, reject) {
      if (!file) { resolve(null); return; }
      var reader = new FileReader();
      reader.onerror = function () { reject(new Error('read_failed')); };
      reader.onload = function () {
        var img = new Image();
        img.onerror = function () { reject(new Error('decode_failed')); };
        img.onload = function () {
          var maxEdge = 1600;
          var w = img.naturalWidth, h = img.naturalHeight;
          var scale = Math.min(1, maxEdge / Math.max(w, h));
          var cw = Math.max(1, Math.round(w * scale));
          var ch = Math.max(1, Math.round(h * scale));
          var canvas = document.createElement('canvas');
          canvas.width = cw;
          canvas.height = ch;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, cw, ch);
          resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.8), name: 'guard.jpg' });
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }

  /** Plain fetch, 15s timeout, never throws — always resolves to {ok, ...}. */
  function submitEscalation(payload) {
    var endpoint = config.escalationEndpoint;
    if (!endpoint) return Promise.resolve({ ok: false, error: 'not_configured' });

    var controller = ('AbortController' in window) ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, 15000) : null;

    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      if (timer) clearTimeout(timer);
      return res.json().catch(function () { return {}; }).then(function (data) {
        data.httpStatus = res.status;
        return data;
      });
    }).catch(function (err) {
      if (timer) clearTimeout(timer);
      return { ok: false, error: err && err.name === 'AbortError' ? 'timeout' : 'network_error' };
    });
  }

  function formField(name, label, type, value, required, placeholder) {
    return '<label class="gg-wz-field">' +
      '<span class="gg-wz-field-label">' + label + (required ? ' *' : '') + '</span>' +
      '<input class="gg-wz-field-input" name="' + name + '" type="' + type + '"' +
      (value ? ' value="' + String(value).replace(/"/g, '&quot;') + '"' : '') +
      (placeholder ? ' placeholder="' + placeholder + '"' : '') +
      (required ? ' required' : '') + '>' +
      '</label>';
  }

  function formTextarea(name, label, value) {
    return '<label class="gg-wz-field">' +
      '<span class="gg-wz-field-label">' + label + '</span>' +
      '<textarea class="gg-wz-field-input gg-wz-field-textarea" name="' + name + '" rows="3">' + (value || '') + '</textarea>' +
      '</label>';
  }

  function formRadioGroup(name, label, options, selected) {
    var html = '<div class="gg-wz-field"><span class="gg-wz-field-label">' + label + '</span><div class="gg-wz-field-radios">';
    options.forEach(function (opt) {
      html += '<label class="gg-wz-radio-pill">' +
        '<input type="radio" name="' + name + '" value="' + opt.v + '"' + (opt.v === selected ? ' checked' : '') + '>' +
        '<span>' + opt.l + '</span></label>';
    });
    html += '</div></div>';
    return html;
  }

  function formToggle(name, label) {
    return '<button type="button" class="gg-wz-field-toggle" data-field="' + name + '" aria-pressed="false">' +
      '<span class="gg-wz-switch" aria-hidden="true"></span><span>' + label + '</span></button>';
  }

  function formPhotoField(required) {
    return '<div class="gg-wz-field">' +
      '<span class="gg-wz-field-label">Photo of the guard' + (required ? ' *' : ' (optional)') + '</span>' +
      '<p class="gg-wz-muted">A photo helps us see exactly what happened. We can usually spot the issue straight away. Use a straight-on photo in good light against a plain background.</p>' +
      '<div class="gg-wz-photo-zone" data-state="empty">' +
      '<img class="gg-wz-photo-preview" alt="">' +
      '<p class="gg-wz-photo-error">We could not read that photo. You can still send without one.</p>' +
      '<label class="gg-wz-btn gg-wz-btn--secondary gg-wz-photo-label">' +
      '<input class="gg-wz-photo-input" type="file" accept="image/*" capture="environment" hidden>' +
      '<span>Take a photo</span>' +
      '</label>' +
      '<button type="button" class="gg-wz-btn gg-wz-btn--text gg-wz-photo-retake">Retake</button>' +
      '</div>' +
      '</div>';
  }

  var Overlay = {
    el: null,
    photoData: null,

    ensure: function () {
      if (this.el) return this.el;
      var root = document.getElementById(ROOT_ID);
      var wrap = document.createElement('div');
      wrap.className = 'gg-wz-overlay';
      wrap.innerHTML = '<div class="gg-wz-overlay-backdrop"></div><div class="gg-wz-overlay-sheet" role="dialog" aria-modal="true"></div>';
      root.appendChild(wrap);
      var self = this;
      wrap.querySelector('.gg-wz-overlay-backdrop').addEventListener('click', function () { self.close(); });
      this.el = wrap;
      return wrap;
    },

    buildForm: function (kind, opts) {
      var isReplacement = kind === 'replacement';
      var title = isReplacement ? 'Free replacement' : 'Need help?';
      var intro = isReplacement
        ? 'We’ll send a fresh pack in the post. It is free.'
        : 'Tell us what happened. We usually reply within a day.';
      var sizeDefault = opts.presetSize || State.get('size', STATE_UNSET_DEFAULTS.size);

      var html = '';
      html += '<div class="gg-wz-overlay-head">';
      html += '<h2 class="gg-wz-title">' + title + '</h2>';
      html += '<button type="button" class="gg-wz-overlay-close" aria-label="Close">✕</button>';
      html += '</div>';
      html += '<div class="gg-wz-overlay-body">';
      html += '<p class="gg-wz-body-text">' + intro + '</p>';
      html += '<form class="gg-wz-form" novalidate>';
      html += formField('email', 'Email', 'email', '', true, 'you@example.com');
      html += formField('orderNumber', 'Order number', 'text', '', isReplacement, isReplacement ? '' : 'Optional');

      if (isReplacement) {
        html += '<div class="gg-wz-form-row">';
        html += formRadioGroup('size', 'Size', [{ v: 'small', l: 'Small' }, { v: 'large', l: 'Large' }], sizeDefault);
        // We always send exactly one 2-pack regardless of the answer here —
        // this is a data point for support/ops, not a fulfillment control,
        // so it asks in terms the customer actually thinks in (guards, not
        // packs) rather than our shipment unit.
        html += formRadioGroup('guardsNeeded', 'How many guards?', [{ v: '1', l: '1' }, { v: '2', l: '2' }], '1');
        html += '</div>';
        html += formToggle('addressChanged', 'Shipping somewhere different this time?');
        html += '<div class="gg-wz-form-address" hidden>' + formTextarea('address', 'New address', '') + '</div>';
      } else {
        html += formField('issue', 'What’s happening', 'text', opts.presetIssue || '', true, 'A short summary');
      }

      // `opts.prompts` — structured questions instead of one generic
      // textarea, for entry points where the answers are worth asking for
      // specifically (care.feedback_placeholder's "what went well / what
      // could we improve"). Someone willing to write private feedback at
      // all is worth a slightly better question than just "anything else."
      if (opts.prompts && opts.prompts.length) {
        opts.prompts.forEach(function (p) {
          html += formTextarea('prompt_' + p.id, p.label, '');
        });
      } else {
        html += formTextarea('message', isReplacement ? 'Anything else to add' : 'Details (optional)', '');
      }
      html += formPhotoField(Boolean(opts.photoRequired));
      html += '<p class="gg-wz-form-error" hidden></p>';
      html += '<button type="submit" class="gg-wz-btn gg-wz-btn--primary gg-wz-form-submit">' + (isReplacement ? 'Send my replacement' : 'Send') + '</button>';
      html += '</form>';
      html += '</div>';
      return html;
    },

    open: function (kind, opts) {
      opts = opts || {};
      var wrap = this.ensure();
      this.photoData = null;
      var sheet = wrap.querySelector('.gg-wz-overlay-sheet');

      // "info" is a read-only static sheet — safety/legal copy, nothing to
      // submit — so it skips buildForm/wire entirely rather than routing
      // through machinery built for the help/replacement forms (which
      // assume a <form>, a photo field, an address toggle, and so on).
      if (kind === 'info') {
        sheet.innerHTML = this.buildInfo(opts);
        this.reveal(wrap);
        var self = this;
        sheet.querySelector('.gg-wz-overlay-close').addEventListener('click', function () { self.close(); });
        // "I understand" at the end of the disclaimer — a second, more
        // deliberate dismiss than the header's ✕, for a screen that's
        // reassurance/compliance copy rather than a quick lookup someone
        // just closes out of.
        var infoDone = sheet.querySelector('.gg-wz-info-done');
        if (infoDone) infoDone.addEventListener('click', function () { self.close(); });
        return;
      }

      sheet.innerHTML = this.buildForm(kind, opts);
      this.reveal(wrap);
      this.wire(sheet, kind, opts);
      track('gg_wizard_escalation_opened', { kind: kind, wizard_step: State.data && State.data.currentStep });
    },

    buildInfo: function (opts) {
      var html = '';
      html += '<div class="gg-wz-overlay-head">';
      html += '<h2 class="gg-wz-title">' + interpolate(opts.title || '') + '</h2>';
      html += '<button type="button" class="gg-wz-overlay-close" aria-label="Close">✕</button>';
      html += '</div>';
      html += '<div class="gg-wz-overlay-body">';
      (opts.body || []).forEach(function (para) {
        html += '<p class="gg-wz-body-text">' + interpolate(para) + '</p>';
      });
      html += '<button type="button" class="gg-wz-btn gg-wz-btn--primary gg-wz-info-done">I understand</button>';
      html += '</div>';
      return html;
    },

    /**
     * PARKED, 15 Aug — not currently called. A review URL, loaded fresh
     * inside an iframe instead of a new tab, was going to sidestep the
     * ReviewAdapter embed's whole problem (a real page load already has the
     * widget server-rendered, no "script scans the DOM once, before our
     * content exists" race) — but confirmed live that judge.me's own domain
     * sends `X-Frame-Options: sameorigin`, which blocks this outright and
     * isn't something any code here can override. Framing failures like
     * that also aren't reliably detectable from JS (a blocked frame just
     * renders blank, no error the parent page can see), so "try the frame,
     * fall back on failure" was never buildable as a silent default. Left
     * in place, not deleted — genuinely useful for a SAME-ORIGIN url (a
     * page on the merchant's own store doesn't send this header), just not
     * for judge.me's own hosted review page specifically. care.review now
     * uses a plain `target="_blank"` link instead (see `reviewLink` in
     * buildActions) — framing restrictions don't apply to a real top-level
     * navigation in a new tab, so that's guaranteed to work regardless of
     * what the destination's headers say.
     */
    openIframe: function (url, opts) {
      opts = opts || {};
      var wrap = this.ensure();
      var sheet = wrap.querySelector('.gg-wz-overlay-sheet');
      sheet.innerHTML =
        '<div class="gg-wz-overlay-head">' +
        '<h2 class="gg-wz-title">' + (opts.title || 'Write a review') + '</h2>' +
        '<button type="button" class="gg-wz-overlay-close" aria-label="Close">✕</button>' +
        '</div>' +
        '<div class="gg-wz-overlay-body gg-wz-overlay-body--frame">' +
        '<iframe class="gg-wz-review-frame" src="' + url + '" title="' + (opts.title || 'Write a review') + '" loading="lazy"></iframe>' +
        '<a class="gg-wz-btn gg-wz-btn--text" href="' + url + '" target="_blank" rel="noopener">Trouble loading? Open in a new tab</a>' +
        '</div>';
      this.reveal(wrap);
      var self = this;
      sheet.querySelector('.gg-wz-overlay-close').addEventListener('click', function () { self.close(); });
      track('gg_wizard_review_frame_opened', {});
    },

    // First open ever: setting the closed AND open class in the same tick
    // can get coalesced into one paint on a brand-new element, skipping the
    // transition entirely rather than animating it — so land on closed,
    // force a reflow, then flip to --open a couple of frames later. Same
    // two-rAF pattern used for the chain-advance progress bar.
    reveal: function (wrap) {
      wrap.className = 'gg-wz-overlay';
      void wrap.offsetHeight;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { wrap.className = 'gg-wz-overlay gg-wz-overlay--open'; });
      });
    },

    // Just removes --open — the backdrop/sheet transitions and the delayed
    // visibility:hidden (see wizard.css) do the rest. No JS timing needed.
    close: function () {
      if (!this.el) return;
      this.el.className = 'gg-wz-overlay';
    },

    wire: function (sheet, kind, opts) {
      var self = this;
      var form = sheet.querySelector('.gg-wz-form');

      sheet.querySelector('.gg-wz-overlay-close').addEventListener('click', function () { self.close(); });

      var addrToggle = sheet.querySelector('[data-field="addressChanged"]');
      var addrWrap = sheet.querySelector('.gg-wz-form-address');
      if (addrToggle && addrWrap) {
        addrToggle.addEventListener('click', function () {
          var pressed = addrToggle.getAttribute('aria-pressed') === 'true';
          addrToggle.setAttribute('aria-pressed', String(!pressed));
          addrWrap.hidden = pressed;
        });
      }

      var photoZone = sheet.querySelector('.gg-wz-photo-zone');
      var photoInput = sheet.querySelector('.gg-wz-photo-input');
      var photoPreview = sheet.querySelector('.gg-wz-photo-preview');
      if (photoInput) {
        photoInput.addEventListener('change', function () {
          var file = photoInput.files && photoInput.files[0];
          if (!file) return;
          photoZone.setAttribute('data-state', 'loading');
          downscalePhoto(file).then(function (photo) {
            self.photoData = photo;
            photoPreview.src = photo.dataUrl;
            photoZone.setAttribute('data-state', 'ready');
          }).catch(function () {
            self.photoData = null;
            photoZone.setAttribute('data-state', 'error');
          });
        });
      }
      var retakeBtn = sheet.querySelector('.gg-wz-photo-retake');
      if (retakeBtn) {
        retakeBtn.addEventListener('click', function () {
          self.photoData = null;
          photoZone.setAttribute('data-state', 'empty');
          if (photoInput) photoInput.value = '';
        });
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submit(form, kind, opts);
      });
    },

    showError: function (errEl, msg) {
      errEl.textContent = msg;
      errEl.hidden = false;
    },

    showSuccess: function (kind, opts) {
      var sheet = this.el.querySelector('.gg-wz-overlay-sheet');
      var body = sheet.querySelector('.gg-wz-overlay-body');
      var continueTo = opts && opts.onSuccessNext;
      var title = continueTo
        ? 'Thanks, we have your message'
        : (kind === 'replacement' ? 'We’ll get that in the post' : 'Got it, thanks');
      var message = continueTo
        ? 'Our team will review it and get back to you, usually within 1 business day.'
        : (kind === 'replacement' ? 'You’ll get a confirmation email once it ships.' : 'We usually reply within a day.');
      body.innerHTML =
        '<div class="gg-wz-overlay-success">' +
        '<p class="gg-wz-title">' + title + '</p>' +
        '<p class="gg-wz-body-text">' + message + '</p>' +
        '<button type="button" class="gg-wz-btn gg-wz-btn--primary gg-wz-overlay-done">' + (continueTo ? 'Continue' : 'Done') + '</button>' +
        '</div>';
      var self = this;
      body.querySelector('.gg-wz-overlay-done').addEventListener('click', function () {
        self.close();
        if (continueTo) Wizard.advance(continueTo);
      });
    },

    submit: function (form, kind, opts) {
      var self = this;
      var errEl = form.querySelector('.gg-wz-form-error');
      var submitBtn = form.querySelector('.gg-wz-form-submit');
      errEl.hidden = true;

      var email = form.querySelector('[name="email"]').value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.showError(errEl, 'That email does not look right. Please check it.');
        return;
      }
      var orderNumber = form.querySelector('[name="orderNumber"]').value.trim();
      if (kind === 'replacement' && !orderNumber) {
        this.showError(errEl, 'Order number is required so we can send the replacement to the right order.');
        return;
      }
      var issueField = form.querySelector('[name="issue"]');
      if (kind === 'help' && issueField && !issueField.value.trim()) {
        this.showError(errEl, 'A quick summary of what’s happening helps us reply with an actual answer, not just more questions.');
        return;
      }
      if (opts.photoRequired && !this.photoData) {
        this.showError(errEl, 'A photo of the guard is required for this one.');
        return;
      }

      var message;
      if (opts.prompts && opts.prompts.length) {
        var parts = [];
        opts.prompts.forEach(function (p) {
          var f = form.querySelector('[name="prompt_' + p.id + '"]');
          var v = f ? f.value.trim() : '';
          if (v) parts.push(p.label + '\n' + v);
        });
        message = parts.join('\n\n');
      } else {
        message = form.querySelector('[name="message"]').value.trim();
      }

      var payload = {
        type: kind,
        email: email,
        orderNumber: orderNumber,
        photo: this.photoData || null,
        context: buildEscalationContext()
      };

      if (kind === 'replacement') {
        var sizeInput = form.querySelector('[name="size"]:checked');
        var guardsInput = form.querySelector('[name="guardsNeeded"]:checked');
        var addrToggle = form.querySelector('[data-field="addressChanged"]');
        // `guardsNeeded` is what the customer answered — good signal for
        // support/ops — but fulfillment is always exactly one 2-pack, so
        // that's what goes in `replacement.quantity` (the field the Lambda
        // actually acts on); the raw answer rides along in the message
        // instead of silently overloading a field with a different meaning.
        if (guardsInput) message = 'Guards needed: ' + guardsInput.value + (message ? '\n\n' + message : '');
        payload.replacement = {
          size: sizeInput ? sizeInput.value : State.get('size', STATE_UNSET_DEFAULTS.size),
          quantity: 1,
          addressChanged: addrToggle ? addrToggle.getAttribute('aria-pressed') === 'true' : false,
          address: form.querySelector('[name="address"]') ? form.querySelector('[name="address"]').value.trim() : ''
        };
      } else {
        payload.issue = form.querySelector('[name="issue"]') ? form.querySelector('[name="issue"]').value.trim() : '';
      }

      payload.message = message;

      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';
      track('gg_wizard_escalation_submit', { kind: kind, wizard_step: State.data && State.data.currentStep });

      submitEscalation(payload).then(function (res) {
        if (res && res.ok) {
          track('gg_wizard_escalation_success', { kind: kind, via: res.via });
          if (kind === 'replacement') {
            // Gates "Okay, got it" on triage.destroyed/.bitthrough/.spent —
            // per CUSTOMER_JOURNEY.md's intent, that button should only be
            // available once a replacement has actually been requested,
            // not as an easy way to dismiss the offer entirely while still
            // out of spares. Silent refresh so the still-hidden-behind-the-
            // overlay step is already correct by the time "Done" closes it.
            State.set({ replacementRequested: true });
            Wizard.refreshCurrentStep();
          }
          self.showSuccess(kind, opts);
        } else {
          submitBtn.disabled = false;
          submitBtn.textContent = kind === 'replacement' ? 'Send my replacement' : 'Send';
          track('gg_wizard_escalation_error', { kind: kind, error: res && res.error });
          self.showError(errEl, 'That did not go through. Please try again. If it keeps failing, reach us directly at ' + (config.supportEmail || 'hello@trygrindguard.com') + '.');
        }
      });
    }
  };

  // ------------------------------------------------------------------
  // Review adapter — Judge.me embed. Phase 7, see ENGINE_SPEC §9.5.
  //
  // PARKED, 15 Aug — not currently called from anywhere. Real-device testing
  // (see build/DEPLOY.md's Phase 7 follow-ups) went three rounds trying to
  // get Judge.me's script to notice a widget div injected after its own
  // initial page-load scan (this wizard is a single-page app, so the div
  // never exists at that scan): a documented `batchRefresh` hook that
  // didn't exist on this install's loader, a generic script-tag
  // re-injection that also didn't get picked up. `care.review` now uses
  // `Overlay.openIframe` instead — a real product page, freshly loaded in
  // an iframe, sidesteps the whole problem (Judge.me's script runs its
  // normal one-time scan against a page that already has the widget
  // server-rendered, same as any real page load). Left in place, not
  // deleted: it's real, tested, working infrastructure, and worth
  // revisiting if Judge.me support ever explains the actual re-init hook —
  // at which point this goes back to being the better experience (no
  // outbound frame at all).
  //
  // There is no storefront-safe way to POST a review directly: Judge.me's
  // write token is server-side only, so a public API call from theme JS
  // would either fail or expose write access to all review data. The only
  // approach that keeps the customer inside the wizard is embedding
  // Judge.me's own widget and letting ITS "Write a Review" control do the
  // work — never simulating a click on one of its internal CSS classes,
  // which is a third-party implementation detail that can silently change
  // between Judge.me versions with no warning to us. Instead: mount the
  // widget, watch whether it actually renders anything, and swap to a
  // plain link if it doesn't — a real fallback, not a dead empty box.
  //
  // Only `judgeme` has a real adapter for v1; `loox`/`none` (and a missing
  // product id) fall straight to isAvailable() === false, which callers use
  // to skip the widget attempt entirely rather than mounting-then-failing.
  // ------------------------------------------------------------------

  var ReviewAdapter = {
    isAvailable: function () {
      var r = config.reviews || {};
      return Boolean(r.enabled) && r.provider === 'judgeme' && Boolean(r.productId);
    },

    fallbackHtml: function () {
      var r = config.reviews || {};
      if (!r.url) {
        return '<p class="gg-wz-muted">Reviews are not available right now. Thanks for considering it.</p>';
      }
      return '<p class="gg-wz-muted">The review widget did not load. You can still leave one directly.</p>' +
        '<a class="gg-wz-btn gg-wz-btn--secondary" href="' + r.url + '" target="_blank" rel="noopener">Write a review</a>';
    },

    /**
     * Mounts the Judge.me shortcode into `container` and polls briefly for
     * it to actually render (script blocked, ad-blocker, app embed not
     * enabled on this page, or a bad product id all show up the same way —
     * nothing appears). Falls back to a plain link if nothing mounts within
     * `timeoutMs`. Safe to call even when `isAvailable()` is false — it
     * just renders the fallback immediately rather than waiting on a widget
     * that was never going to appear.
     */
    mount: function (container, opts) {
      opts = opts || {};
      if (!this.isAvailable()) {
        container.innerHTML = this.fallbackHtml();
        return;
      }

      var r = config.reviews;
      // "A photo of your mold really helps other people" (CUSTOMER_JOURNEY.md
      // §10) — merchant-toggleable copy, not the widget's own photo upload
      // control (Judge.me's form has that natively regardless). Routed
      // through interpolate() so {{mold}} resolves to "mould" for
      // GB/AU/NZ/IE — this string lives here, not in wizard-flow.json, so
      // it doesn't get that for free the way flow copy does.
      var photoHint = r.requestPhoto
        ? '<p class="gg-wz-muted">' + interpolate('A photo of your {{mold}} helps other people. You are already holding it.') + '</p>'
        : '';
      container.innerHTML = photoHint + '<div class="jdgm-widget jdgm-review-widget" data-id="' + r.productId + '"></div>';

      // Judge.me's loader scans the DOM for `.jdgm-widget` once, on initial
      // page load — confirmed live: `window.jdgm exists: true` but nothing
      // rendered, meaning the script itself is fine, it just never saw this
      // div. Since the wizard is a single-page app (this div is injected
      // well after that initial scan, every time a customer reaches this
      // step), it needs an explicit re-scan.
      //
      // `batchRefresh` was the first guess (Judge.me's documented hook for
      // AJAX-loaded/dynamically-inserted content) — confirmed live it's NOT
      // present on `window.jdgm` for this install (`batchRefresh available:
      // false`), so either this version names it differently, or — the
      // reason this is now a retry rather than a one-shot call — `window.
      // jdgm` is a stub object that exists immediately but has its real
      // methods attached asynchronously once the full script finishes
      // loading, in which case calling again a moment later would work
      // without needing a different name at all. Retried on every poll tick
      // rather than once, cheaply covering that theory for free.
      function tryRefresh() {
        if (window.jdgm && typeof window.jdgm.batchRefresh === 'function') {
          window.jdgm.batchRefresh();
          return true;
        }
        return false;
      }

      // Confirmed live this install's `window.jdgm` is Judge.me's v3 loader
      // stub (`isVersion3: true`) — it only exposes generic loading
      // utilities (loadJS/loadScript/docReady/widgetPath), no method that
      // looks like a public "rescan for new widgets" hook, so there's
      // nothing safe to call by name. Instead of guessing at those unknown
      // internals: re-inject Judge.me's own `<script>` tag(s) wholesale.
      // Whatever they do on a normal page load already works (that's how
      // `window.jdgm` got here) — doing it again now that the widget div
      // exists should pick it up the same way a fresh page load would, with
      // zero knowledge of their internal API required. Not guaranteed: if
      // their script guards its own init behind a one-time flag (plausible,
      // given `_loaderExecuted` is tracked explicitly), a second run could
      // be a no-op for the part that matters — but it costs nothing to try,
      // and it's a well-established generic pattern for exactly this
      // "third-party widget script vs. SPA" mismatch.
      function reinjectJudgeMeScript() {
        var existing = document.querySelectorAll('script[src*="judge" i]');
        if (!existing.length) return false;
        existing.forEach(function (old) {
          var fresh = document.createElement('script');
          fresh.src = old.src;
          fresh.async = true;
          document.body.appendChild(fresh);
        });
        return true;
      }

      tryRefresh();
      var foundScriptToReinject = reinjectJudgeMeScript();

      var checkMs = 250;
      // 7s, not 4 — a script that loads via content_for_header can genuinely
      // take a few seconds on a slow connection, and a false "didn't load"
      // when it would have arrived a second later is worse than a slightly
      // longer wait before the fallback shows.
      var maxMs = opts.timeoutMs || 7000;
      var waited = 0;
      var settled = false;
      var reinjectedAgain = false;
      var self = this;
      var poll = setInterval(function () {
        waited += checkMs;
        tryRefresh();
        // One more attempt partway through, in case the first re-injected
        // script hadn't finished loading yet when it ran — not on every
        // tick, since repeatedly re-executing a third-party script has real
        // costs (duplicate listeners, wasted requests) this doesn't.
        if (!reinjectedAgain && waited >= 2000) {
          reinjectedAgain = true;
          reinjectJudgeMeScript();
        }
        var widget = container.querySelector('.jdgm-widget');
        // `.children.length` alone misses a widget that renders text nodes
        // before it renders elements; textContent catches that too.
        var rendered = widget && (widget.children.length > 0 || widget.textContent.trim().length > 0);
        if (rendered) {
          settled = true;
          clearInterval(poll);
          track('gg_wizard_review_widget_mounted', {});
        } else if (waited >= maxMs) {
          clearInterval(poll);
          // Left for whoever's diagnosing this against the live store — not
          // shown to the customer. window.jdgm missing → the script never
          // loaded (check the app embed toggle). window.jdgm present but no
          // batchRefresh → this Judge.me version exposes re-init under a
          // different name, worth checking their current docs. Both present
          // and it still didn't render → likely the product id itself.
          console.warn(
            '[wizard] Judge.me widget did not render within ' + maxMs + 'ms for product id "' + r.productId + '". ' +
            'window.jdgm exists: ' + (typeof window.jdgm !== 'undefined') + '. ' +
            'batchRefresh available: ' + Boolean(window.jdgm && typeof window.jdgm.batchRefresh === 'function') + '. ' +
            'script tag(s) found to reinject: ' + foundScriptToReinject + '. ' +
            'Check the Judge.me app embed is enabled for this theme, and that the product id is correct.'
          );
          container.innerHTML = self.fallbackHtml();
          track('gg_wizard_review_widget_fallback', {});
        }
      }, checkMs);
    }
  };

  // ------------------------------------------------------------------
  // Chrome wiring & entry point
  // ------------------------------------------------------------------

  function wireChrome(root) {
    root.addEventListener('pointerdown', function once() {
      cues.unlock();
      root.removeEventListener('pointerdown', once);
    }, { once: true });

    var mute = root.querySelector('[data-gg-wz="mute"]');
    if (mute) {
      mute.setAttribute('aria-pressed', String(prefs.sound));
      mute.addEventListener('click', function () {
        prefs.sound = !prefs.sound;
        prefs.haptic = prefs.sound;
        prefs.asked = true;
        savePrefs();
        mute.setAttribute('aria-pressed', String(prefs.sound));
        if (prefs.sound) { cues.beep(880, 120); cues.buzz(30); }
        track('gg_wizard_pref_sound', { enabled: prefs.sound });
        Wizard.refreshIfPrefsStep();
      });
    }

    // General "get help" — no diagnosis to pre-fill, no reason to assume a
    // photo's relevant (could be any question), so it opens with an empty
    // form and an optional photo, unlike the triage-specific entry points.
    var help = root.querySelector('[data-gg-wz="help"]');
    if (help) {
      help.addEventListener('click', function () {
        Overlay.open('help', { photoRequired: false });
      });
    }
  }

  function main() {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;
    initViewport(root);
    attemptChromeCollapse();
    wireChrome(root);
    boot(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', main);
  } else {
    main();
  }
})();
